

// src/routes/AppRouter.js
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense } from 'react';
import Spinner from '../components/Spinner';
import ProtectedRoute from './ProtectedRoute';
import MainLayout from "../components/MainLayout";
const HomePage = React.lazy(() => import('../pages/HomePage'));
const LoginPage = React.lazy(() => import('../pages/LoginPage'));
const RegisterPage = React.lazy(() => import('../pages/RegisterPage'));
const ForgotPasswordPage = React.lazy(() => import('../pages/ForgotPasswordPage'));
const EmployeeDashboard = React.lazy(() => import('../pages/EmployeeDashboard'));
const EmployeeProfile = React.lazy(() => import('../pages/EmployeeProfile'));
const HRDashboard = React.lazy(() => import('../pages/HRDashboard'));
const HREmployeeView = React.lazy(() => import('../pages/HREmployeeView'));
const NotFoundPage = React.lazy(() => import('../pages/NotFoundPage'));

const PartnerSignupPage = React.lazy(() => import('../pages/PartnerSignupPage'));
const PartnerDashboard = React.lazy(() => import('../pages/PartnerDashboard'));
const PartnerManagementPage = React.lazy(() => import('../pages/PartnerManagementPage'));

const RegisterHRPage = React.lazy(() => import('../pages/registerHRPage'));
const RegisterAdminPage = React.lazy(() => import('../pages/RegisterAdminPage'));
const TaskManagementPage = React.lazy(() => import('../pages/TaskManagementPage'));
const LeadsPage = React.lazy(() => import('../pages/LeadsPage'));
const MeetingsPage = React.lazy(() => import('../pages/MeetingsPage'));
const ExpensesPage = React.lazy(() => import('../pages/ExpensesPage'));
const ProjectsPage = React.lazy(() => import('../pages/ProjectsPage'));
const AssetsPage = React.lazy(() => import('../pages/AssetsPage'));
const InvoicesPage = React.lazy(() => import('../pages/InvoicesPage'));
const KPIsPage = React.lazy(() => import('../pages/KPIsPage'));
const PortalRequestsPage = React.lazy(() => import('../pages/PortalRequestsPage'));
const TaskPerformancePage = React.lazy(() => import('../pages/TaskPerformancePage'));
const EODReportsPage = React.lazy(() => import('../pages/EODReportsPage'));
const ManageEmployeesPage = React.lazy(() => import('../pages/ManageEmployeesPage'));
const SalaryCalculatorPage = React.lazy(() => import('../pages/SalaryCalculatorPage'));
const EmployeeAnnouncements = React.lazy(() => import('../pages/EmployeeAnnouncements'));
const AnnouncementsPage = React.lazy(() => import('../pages/AnnouncmentsPage'));
const AnalyticsDashboard = React.lazy(() => import('../pages/AnalyticsDashbaord'));
const GetDataPage = React.lazy(() => import('../pages/GetDataPage'));
const ApplyForLeavePage = React.lazy(() => import('../pages/ApplyForLeavePage'));
const ApproveLeavePage = React.lazy(() => import('../pages/ApproveLeavePage'));

const RankingsPage = React.lazy(() => import('../pages/RankingsPage'));
const PenaltiesPage = React.lazy(() => import('../pages/PenaltiesPage'));
const PrivacyPolicyPage = React.lazy(() => import('../pages/PrivacyPolicyPage'));
const SalarySlipManagement = React.lazy(() => import('../pages/SalarySlipManagement'));
const EmployeeSalarySlips = React.lazy(() => import('../pages/EmployeeSalarySlips'));
const RegisterEmployeeHRPage = React.lazy(() => import('../pages/RegisterEmployeeHRPage'));
const ManageManagersPage = React.lazy(() => import('../pages/ManageManagersPage'));
const FinancePage = React.lazy(() => import('../pages/FinancePage'));
const CallsPage = React.lazy(() => import('../pages/CallsPage'));
const TargetsPage = React.lazy(() => import('../pages/TargetsPage'));
const PartnerCommissionsPage = React.lazy(() => import('../pages/PartnerCommissionsPage'));
const EmailPage = React.lazy(() => import('../pages/EmailPage'));
const HolidayCalendarPage = React.lazy(() => import('../pages/HolidayCalendarPage'));
const WhatsAppManagePage = React.lazy(() => import('../pages/WhatsAppManagePage'));

// SEO landing pages — public, no auth, self-contained layout
const HrPortalPage = React.lazy(() => import('../pages/seo/HrPortalPage'));
const HrmsSoftwarePage = React.lazy(() => import('../pages/seo/HrmsSoftwarePage'));
const PayrollSoftwarePage = React.lazy(() => import('../pages/seo/PayrollSoftwarePage'));
const AttendancePage = React.lazy(() => import('../pages/seo/AttendancePage'));
const LeavePage = React.lazy(() => import('../pages/seo/LeavePage'));
const EmployeeManagementPage = React.lazy(() => import('../pages/seo/EmployeeManagementPage'));
const EmployeePortalPage = React.lazy(() => import('../pages/seo/EmployeePortalPage'));
const CrmPage = React.lazy(() => import('../pages/seo/CrmPage'));
const CrmSmallBusinessPage = React.lazy(() => import('../pages/seo/CrmSmallBusinessPage'));
const WorkforcePage = React.lazy(() => import('../pages/seo/WorkforcePage'));
const ProjectManagementPage = React.lazy(() => import('../pages/seo/ProjectManagementPage'));
const BusinessOsPage = React.lazy(() => import('../pages/seo/BusinessOsPage'));
const BusinessManagementPage = React.lazy(() => import('../pages/seo/BusinessManagementPage'));
const KekaAlternativePage = React.lazy(() => import('../pages/seo/KekaAlternativePage'));
const GreythrAlternativePage = React.lazy(() => import('../pages/seo/GreythrAlternativePage'));
const DarwinboxAlternativePage = React.lazy(() => import('../pages/seo/DarwinboxAlternativePage'));
const ZohoPeoplePage = React.lazy(() => import('../pages/seo/ZohoPeoplePage'));
const SumhrAlternativePage = React.lazy(() => import('../pages/seo/SumhrAlternativePage'));
const FreshteamAlternativePage = React.lazy(() => import('../pages/seo/FreshteamAlternativePage'));
const SalaryBoxAlternativePage = React.lazy(() => import('../pages/seo/SalaryBoxAlternativePage'));
const PetpoojaAlternativePage = React.lazy(() => import('../pages/seo/PetpoojaAlternativePage'));
const KredilyAlternativePage = React.lazy(() => import('../pages/seo/KredilyAlternativePage'));
const VyaparAlternativePage = React.lazy(() => import('../pages/seo/VyaparAlternativePage'));
const ManufacturingPage = React.lazy(() => import('../pages/seo/ManufacturingPage'));
const ItCompanyPage = React.lazy(() => import('../pages/seo/ItCompanyPage'));
const FreeHrmsPage = React.lazy(() => import('../pages/seo/FreeHrmsPage'));
const SalarySlipPage = React.lazy(() => import('../pages/seo/SalarySlipPage'));
const MumbaiPage = React.lazy(() => import('../pages/seo/MumbaiPage'));
const BangalorePage = React.lazy(() => import('../pages/seo/BangalorePage'));
const DelhiPage = React.lazy(() => import('../pages/seo/DelhiPage'));
const SmallBusinessPage = React.lazy(() => import('../pages/seo/SmallBusinessPage'));
const StartupsPage = React.lazy(() => import('../pages/seo/StartupsPage'));
const PerformanceManagementPage = React.lazy(() => import('../pages/seo/PerformanceManagementPage'));
// City landing pages
const NoidaPage = React.lazy(() => import('../pages/seo/NoidaPage'));
const GurgaonPage = React.lazy(() => import('../pages/seo/GurgaonPage'));
const PunePage = React.lazy(() => import('../pages/seo/PunePage'));
const HyderabadPage = React.lazy(() => import('../pages/seo/HyderabadPage'));
const ChennaiPage = React.lazy(() => import('../pages/seo/ChennaiPage'));
const AhmedabadPage = React.lazy(() => import('../pages/seo/AhmedabadPage'));
const RohtakPage = React.lazy(() => import('../pages/seo/RohtakPage'));
const HrSoftwareLucknowPage = React.lazy(() => import('../pages/seo/HrSoftwareLucknowPage'));
const HrSoftwareAgraPage = React.lazy(() => import('../pages/seo/HrSoftwareAgraPage'));
const HrSoftwareMeerutPage = React.lazy(() => import('../pages/seo/HrSoftwareMeerutPage'));
const HrSoftwareKanpurPage = React.lazy(() => import('../pages/seo/HrSoftwareKanpurPage'));
const HrSoftwareVaranasiPage = React.lazy(() => import('../pages/seo/HrSoftwareVaranasiPage'));
const HrSoftwareGhaziabadPage = React.lazy(() => import('../pages/seo/HrSoftwareGhaziabadPage'));
const HrSoftwareGreaterNoidaPage = React.lazy(() => import('../pages/seo/HrSoftwareGreaterNoidaPage'));
const HrSoftwareAllahabadPage = React.lazy(() => import('../pages/seo/HrSoftwareAllahabadPage'));
const HrSoftwareJhajjarPage = React.lazy(() => import('../pages/seo/HrSoftwareJhajjarPage'));
const HrSoftwareRewariPage = React.lazy(() => import('../pages/seo/HrSoftwareRewariPage'));
const HrSoftwareBahadurgarhPage = React.lazy(() => import('../pages/seo/HrSoftwareBahadurgarhPage'));
const HrSoftwareBhiwaniPage = React.lazy(() => import('../pages/seo/HrSoftwareBhiwaniPage'));
const HrSoftwareSirsaPage = React.lazy(() => import('../pages/seo/HrSoftwareSirsaPage'));
const HrSoftwareJindPage = React.lazy(() => import('../pages/seo/HrSoftwareJindPage'));
const HrSoftwareKurukshetraPage = React.lazy(() => import('../pages/seo/HrSoftwareKurukshetraPage'));
const HrSoftwarePanchkulaPage = React.lazy(() => import('../pages/seo/HrSoftwarePanchkulaPage'));
const HrSoftwareYamunanagarPage = React.lazy(() => import('../pages/seo/HrSoftwareYamunanagarPage'));
const HrSoftwarePalwalPage = React.lazy(() => import('../pages/seo/HrSoftwarePalwalPage'));
const HrSoftwareMaesarPage = React.lazy(() => import('../pages/seo/HrSoftwareMaesarPage'));
const HrSoftwareFaridabadNcrPage = React.lazy(() => import('../pages/seo/HrSoftwareFaridabadNcrPage'));
const HrSoftwareDelhiNcrPage = React.lazy(() => import('../pages/seo/HrSoftwareDelhiNcrPage'));
const HrSoftwareWestDelhiPage = React.lazy(() => import('../pages/seo/HrSoftwareWestDelhiPage'));
const HrSoftwareSouthDelhiPage = React.lazy(() => import('../pages/seo/HrSoftwareSouthDelhiPage'));
const HrSoftwareEastDelhiPage = React.lazy(() => import('../pages/seo/HrSoftwareEastDelhiPage'));
// State landing pages
const HaryanaPage = React.lazy(() => import('../pages/seo/HaryanaPage'));
const KeralaPage = React.lazy(() => import('../pages/seo/KeralaPage'));
const KarnatakaPage = React.lazy(() => import('../pages/seo/KarnatakaPage'));
const MaharashtraPage = React.lazy(() => import('../pages/seo/MaharashtraPage'));
const TamilNaduPage = React.lazy(() => import('../pages/seo/TamilNaduPage'));
// New product-category SEO pages (Jun 2026)
const ExpenseManagementPage = React.lazy(() => import('../pages/seo/ExpenseManagementPage'));
const AssetManagementPage = React.lazy(() => import('../pages/seo/AssetManagementPage'));
const KpiSoftwarePage = React.lazy(() => import('../pages/seo/KpiSoftwarePage'));
const OkrSoftwarePage = React.lazy(() => import('../pages/seo/OkrSoftwarePage'));
const TaskManagementSeoPage = React.lazy(() => import('../pages/seo/TaskManagementPage'));
const MeetingManagementPage = React.lazy(() => import('../pages/seo/MeetingManagementPage'));
const GstInvoiceSoftwarePage = React.lazy(() => import('../pages/seo/GstInvoiceSoftwarePage'));
const EmployeeEngagementPage = React.lazy(() => import('../pages/seo/EmployeeEngagementPage'));
const InternalCommunicationPage = React.lazy(() => import('../pages/seo/InternalCommunicationPage'));
const TeamManagementPage = React.lazy(() => import('../pages/seo/TeamManagementPage'));
const StaffManagementPage = React.lazy(() => import('../pages/seo/StaffManagementPage'));
const PeopleManagementPage = React.lazy(() => import('../pages/seo/PeopleManagementPage'));
const OnboardingSoftwarePage = React.lazy(() => import('../pages/seo/OnboardingSoftwarePage'));
const AttendanceSoftwareIndiaPage = React.lazy(() => import('../pages/seo/AttendanceSoftwareIndiaPage'));
const EmployeeAttendancePage = React.lazy(() => import('../pages/seo/EmployeeAttendancePage'));
const OnlineAttendancePage = React.lazy(() => import('../pages/seo/OnlineAttendancePage'));
const AttendanceTrackingPage = React.lazy(() => import('../pages/seo/AttendanceTrackingPage'));
const LeaveManagementSystemPage = React.lazy(() => import('../pages/seo/LeaveManagementSystemPage'));
const OnlineLeaveManagementPage = React.lazy(() => import('../pages/seo/OnlineLeaveManagementPage'));
const PayrollSmallBusinessPage = React.lazy(() => import('../pages/seo/PayrollSmallBusinessPage'));
const OnlinePayrollPage = React.lazy(() => import('../pages/seo/OnlinePayrollPage'));
const RecruitmentSoftwarePage = React.lazy(() => import('../pages/seo/RecruitmentSoftwarePage'));
const AtsSoftwarePage = React.lazy(() => import('../pages/seo/AtsSoftwarePage'));
const SalesPipelinePage = React.lazy(() => import('../pages/seo/SalesPipelinePage'));
// AI keyword SEO pages (Jul 2026)
const AiHrSoftwarePage = React.lazy(() => import('../pages/seo/AiHrSoftwarePage'));
const AiHrmsPage = React.lazy(() => import('../pages/seo/AiHrmsPage'));
const AiPoweredHrPortalPage = React.lazy(() => import('../pages/seo/AiPoweredHrPortalPage'));
const AiHrAssistantPage = React.lazy(() => import('../pages/seo/AiHrAssistantPage'));
const AiHrSoftwareIndiaPage = React.lazy(() => import('../pages/seo/AiHrSoftwareIndiaPage'));
// About page
const AboutPage = React.lazy(() => import('../pages/AboutPage'));
// Blog admin pages
const BlogManage = React.lazy(() => import('../pages/blog/BlogManage'));
const BlogEditor = React.lazy(() => import('../pages/blog/BlogEditor'));
const BlogAccess = React.lazy(() => import('../pages/blog/BlogAccess'));
// Reviews
const ReviewsPage = React.lazy(() => import('../pages/ReviewsPage'));
const ReviewsManage = React.lazy(() => import('../pages/ReviewsManage'));
// New city SEO pages — North Haryana
const FaridabadPage = React.lazy(() => import('../pages/seo/FaridabadPage'));
const PanipatPage = React.lazy(() => import('../pages/seo/PanipatPage'));
const KarnalPage = React.lazy(() => import('../pages/seo/KarnalPage'));
const SonipatPage = React.lazy(() => import('../pages/seo/SonipatPage'));
const HisarPage = React.lazy(() => import('../pages/seo/HisarPage'));
const AmbalaPage = React.lazy(() => import('../pages/seo/AmbalaPage'));
const PanchkulaPage = React.lazy(() => import('../pages/seo/PanchkulaPage'));
const YamunanagarPage = React.lazy(() => import('../pages/seo/YamunanagarPage'));
// New city SEO pages — NCR + North India
const GreaterNoidaPage = React.lazy(() => import('../pages/seo/GreaterNoidaPage'));
const GhaziabadPage = React.lazy(() => import('../pages/seo/GhaziabadPage'));
const MeerutPage = React.lazy(() => import('../pages/seo/MeerutPage'));
const ChandigarhPage = React.lazy(() => import('../pages/seo/ChandigarhPage'));
const LudhianaPage = React.lazy(() => import('../pages/seo/LudhianaPage'));
const JaipurPage = React.lazy(() => import('../pages/seo/JaipurPage'));
const IndorePage = React.lazy(() => import('../pages/seo/IndorePage'));
const CoimbatorePage = React.lazy(() => import('../pages/seo/CoimbatorePage'));
// New city SEO pages — South + West India
const KochiPage = React.lazy(() => import('../pages/seo/KochiPage'));
const SuratPage = React.lazy(() => import('../pages/seo/SuratPage'));
const NagpurPage = React.lazy(() => import('../pages/seo/NagpurPage'));
const LucknowPage = React.lazy(() => import('../pages/seo/LucknowPage'));
const BhopalPage = React.lazy(() => import('../pages/seo/BhopalPage'));
const VisakhapatnamPage = React.lazy(() => import('../pages/seo/VisakhapatnamPage'));
const VadodaraPage = React.lazy(() => import('../pages/seo/VadodaraPage'));
// New keyword SEO pages
const HrmsPage = React.lazy(() => import('../pages/seo/HrmsPage'));
const HrSoftwareIndiaPage = React.lazy(() => import('../pages/seo/HrSoftwareIndiaPage'));
const BestHrmsIndiaPage = React.lazy(() => import('../pages/seo/BestHrmsIndiaPage'));
const CloudHrPage = React.lazy(() => import('../pages/seo/CloudHrPage'));
const ErpSoftwarePage = React.lazy(() => import('../pages/seo/ErpSoftwarePage'));
const HrManagementPage = React.lazy(() => import('../pages/seo/HrManagementPage'));
const PayrollManagementPage = React.lazy(() => import('../pages/seo/PayrollManagementPage'));
const BiometricAlternativePage = React.lazy(() => import('../pages/seo/BiometricAlternativePage'));
// New state SEO pages
const PunjabPage = React.lazy(() => import('../pages/seo/PunjabPage'));
const RajasthanPage = React.lazy(() => import('../pages/seo/RajasthanPage'));
const UtterPradeshPage = React.lazy(() => import('../pages/seo/UtterPradeshPage'));
const TelanganaPage = React.lazy(() => import('../pages/seo/TelanganaPage'));
const GujaratPage = React.lazy(() => import('../pages/seo/GujaratPage'));
// New industry SEO pages
const RetailPage = React.lazy(() => import('../pages/seo/RetailPage'));
const HealthcarePage = React.lazy(() => import('../pages/seo/HealthcarePage'));
const EducationPage = React.lazy(() => import('../pages/seo/EducationPage'));
const ConstructionPage = React.lazy(() => import('../pages/seo/ConstructionPage'));
// CRM sub-category SEO pages
const ContactManagementPage = React.lazy(() => import('../pages/seo/ContactManagementPage'));
const SalesCrmPage = React.lazy(() => import('../pages/seo/SalesCrmPage'));
const LeadManagementPage = React.lazy(() => import('../pages/seo/LeadManagementPage'));
const CustomerManagementPage = React.lazy(() => import('../pages/seo/CustomerManagementPage'));
// Finance SEO pages (Jun 2026)
const InvoicingSoftwarePage = React.lazy(() => import('../pages/seo/InvoicingSoftwarePage'));
const BillingSoftwarePage = React.lazy(() => import('../pages/seo/BillingSoftwarePage'));
const FinanceManagementPage = React.lazy(() => import('../pages/seo/FinanceManagementPage'));
const BusinessFinancePage = React.lazy(() => import('../pages/seo/BusinessFinancePage'));
// Calculator / tool pages (Jun 2026)
const PfCalculatorPage = React.lazy(() => import('../pages/seo/PfCalculatorPage'));
const EsiCalculatorPage = React.lazy(() => import('../pages/seo/EsiCalculatorPage'));
const GratuityCalculatorPage = React.lazy(() => import('../pages/seo/GratuityCalculatorPage'));
const SalaryCalculatorToolPage = React.lazy(() => import('../pages/seo/SalaryCalculatorToolPage'));
const CtcCalculatorPage = React.lazy(() => import('../pages/seo/CtcCalculatorPage'));
const SalarySlipGeneratorPage = React.lazy(() => import('../pages/seo/SalarySlipGeneratorPage'));
const RentReceiptGeneratorPage = React.lazy(() => import('../pages/seo/RentReceiptGeneratorPage'));
const BalanceSheetMakerPage = React.lazy(() => import('../pages/seo/BalanceSheetMakerPage'));
const ProfitLossStatementPage = React.lazy(() => import('../pages/seo/ProfitLossStatementPage'));
const SalaryCertificateGeneratorPage = React.lazy(() => import('../pages/seo/SalaryCertificateGeneratorPage'));
const ExperienceLetterGeneratorPage = React.lazy(() => import('../pages/seo/ExperienceLetterGeneratorPage'));
const FullFinalSettlementCalculatorPage = React.lazy(() => import('../pages/seo/FullFinalSettlementCalculatorPage'));
const OfferLetterGeneratorPage = React.lazy(() => import('../pages/seo/OfferLetterGeneratorPage'));
const AppointmentLetterGeneratorPage = React.lazy(() => import('../pages/seo/AppointmentLetterGeneratorPage'));
const IncrementLetterGeneratorPage = React.lazy(() => import('../pages/seo/IncrementLetterGeneratorPage'));
const InternshipCertificateGeneratorPage = React.lazy(() => import('../pages/seo/InternshipCertificateGeneratorPage'));
const FreeToolsPage = React.lazy(() => import('../pages/seo/FreeToolsPage'));
const NoticePeriodCalculatorPage = React.lazy(() => import('../pages/seo/NoticePeriodCalculatorPage'));
const EmployeeKpiReportMakerPage = React.lazy(() => import('../pages/seo/EmployeeKpiReportMakerPage'));
const LeaveCalculatorPage = React.lazy(() => import('../pages/seo/LeaveCalculatorPage'));
const TdsCalculatorPage = React.lazy(() => import('../pages/seo/TdsCalculatorPage'));
const HraCalculatorPage = React.lazy(() => import('../pages/seo/HraCalculatorPage'));
const IncomeTaxCalculatorPage = React.lazy(() => import('../pages/seo/IncomeTaxCalculatorPage'));
const CtcToInHandCalculatorPage = React.lazy(() => import('../pages/seo/CtcToInHandCalculatorPage'));
const ProfessionalTaxCalculatorPage = React.lazy(() => import('../pages/seo/ProfessionalTaxCalculatorPage'));
const BonusCalculatorPage = React.lazy(() => import('../pages/seo/BonusCalculatorPage'));
const LeaveEncashmentCalculatorPage = React.lazy(() => import('../pages/seo/LeaveEncashmentCalculatorPage'));
const OvertimeCalculatorPage = React.lazy(() => import('../pages/seo/OvertimeCalculatorPage'));
const MinimumWageCalculatorPage = React.lazy(() => import('../pages/seo/MinimumWageCalculatorPage'));
const SalaryHikeCalculatorPage = React.lazy(() => import('../pages/seo/SalaryHikeCalculatorPage'));
const LoanEmiCalculatorPage = React.lazy(() => import('../pages/seo/LoanEmiCalculatorPage'));
// New SEO & AEO Target Pages
const GreytHRvsKekavsAvani = React.lazy(() => import('../pages/seo/GreytHRvsKekavsAvani'));
const PayrollComplianceHub = React.lazy(() => import('../pages/seo/PayrollComplianceHub'));
const GstInvoicingSoftware = React.lazy(() => import('../pages/seo/GstInvoicingSoftware'));
const ShiftManagementSoftware = React.lazy(() => import('../pages/seo/ShiftManagementSoftware'));

// 10 new Email CRM SEO Pages
const BulkEmailSenderPage = React.lazy(() => import('../pages/seo/BulkEmailSenderPage'));
const EmailCrmSoftwarePage = React.lazy(() => import('../pages/seo/EmailCrmSoftwarePage'));
const EmailCampaignSoftwarePage = React.lazy(() => import('../pages/seo/EmailCampaignSoftwarePage'));
const BulkEmailWithoutBlockingPage = React.lazy(() => import('../pages/seo/BulkEmailWithoutBlockingPage'));
const EmailMarketingSoftwareIndiaPage = React.lazy(() => import('../pages/seo/EmailMarketingSoftwareIndiaPage'));
const ColdEmailSoftwarePage = React.lazy(() => import('../pages/seo/ColdEmailSoftwarePage'));
const BulkEmailAnalyticsPage = React.lazy(() => import('../pages/seo/BulkEmailAnalyticsPage'));
const NewsletterCreatorPage = React.lazy(() => import('../pages/seo/NewsletterCreatorPage'));
const EmailDeliverabilitySoftwarePage = React.lazy(() => import('../pages/seo/EmailDeliverabilitySoftwarePage'));
const MassEmailSenderIndiaPage = React.lazy(() => import('../pages/seo/MassEmailSenderIndiaPage'));


// Pricing
const PricingPage = React.lazy(() => import('../pages/PricingPage'));
// International SEO pages (Jun 2026)
const HrSoftwareDubaiPage = React.lazy(() => import('../pages/seo/HrSoftwareDubaiPage'));
const HrSoftwareUaePage = React.lazy(() => import('../pages/seo/HrSoftwareUaePage'));
const HrSoftwareSaudiPage = React.lazy(() => import('../pages/seo/HrSoftwareSaudiPage'));
const HrSoftwareQatarPage = React.lazy(() => import('../pages/seo/HrSoftwareQatarPage'));
const HrSoftwareUsaPage = React.lazy(() => import('../pages/seo/HrSoftwareUsaPage'));
const HrSoftwareUkPage = React.lazy(() => import('../pages/seo/HrSoftwareUkPage'));
const HrSoftwareAustraliaPage = React.lazy(() => import('../pages/seo/HrSoftwareAustraliaPage'));
const HrSoftwareSingaporePage = React.lazy(() => import('../pages/seo/HrSoftwareSingaporePage'));
const HrSoftwareCanadaPage = React.lazy(() => import('../pages/seo/HrSoftwareCanadaPage'));
const HrSoftwareNewZealandPage = React.lazy(() => import('../pages/seo/HrSoftwareNewZealandPage'));
const HrSoftwareIrelandPage = React.lazy(() => import('../pages/seo/HrSoftwareIrelandPage'));
const HrSoftwareSouthAfricaPage = React.lazy(() => import('../pages/seo/HrSoftwareSouthAfricaPage'));
const HrSoftwareNigeriaPage = React.lazy(() => import('../pages/seo/HrSoftwareNigeriaPage'));
const HrSoftwareKenyaPage = React.lazy(() => import('../pages/seo/HrSoftwareKenyaPage'));
const HrSoftwarePhilippinesPage = React.lazy(() => import('../pages/seo/HrSoftwarePhilippinesPage'));
const HrSoftwareMalaysiaPage = React.lazy(() => import('../pages/seo/HrSoftwareMalaysiaPage'));
const HrSoftwareBangladeshPage = React.lazy(() => import('../pages/seo/HrSoftwareBangladeshPage'));
const HrSoftwareIndonesiaPage = React.lazy(() => import('../pages/seo/HrSoftwareIndonesiaPage'));
const HrSoftwareThailandPage = React.lazy(() => import('../pages/seo/HrSoftwareThailandPage'));
const HrSoftwareVietnamPage = React.lazy(() => import('../pages/seo/HrSoftwareVietnamPage'));
const HrSoftwareSriLankaPage = React.lazy(() => import('../pages/seo/HrSoftwareSriLankaPage'));
const HrSoftwareNepalPage = React.lazy(() => import('../pages/seo/HrSoftwareNepalPage'));
const HrSoftwareKuwaitPage = React.lazy(() => import('../pages/seo/HrSoftwareKuwaitPage'));
const HrSoftwareBahrainPage = React.lazy(() => import('../pages/seo/HrSoftwareBahrainPage'));
const HrSoftwareOmanPage = React.lazy(() => import('../pages/seo/HrSoftwareOmanPage'));
const HrSoftwareJordanPage = React.lazy(() => import('../pages/seo/HrSoftwareJordanPage'));
const HrSoftwareEgyptPage = React.lazy(() => import('../pages/seo/HrSoftwareEgyptPage'));
const HrSoftwareTurkeyPage = React.lazy(() => import('../pages/seo/HrSoftwareTurkeyPage'));
const HrSoftwareGermanyPage = React.lazy(() => import('../pages/seo/HrSoftwareGermanyPage'));
const HrSoftwareNetherlandsPage = React.lazy(() => import('../pages/seo/HrSoftwareNetherlandsPage'));
const HrSoftwareMexicoPage = React.lazy(() => import('../pages/seo/HrSoftwareMexicoPage'));
const HrSoftwareBrazilPage = React.lazy(() => import('../pages/seo/HrSoftwareBrazilPage'));
const HrSoftwareJapanPage = React.lazy(() => import('../pages/seo/HrSoftwareJapanPage'));
const HrSoftwareHongKongPage = React.lazy(() => import('../pages/seo/HrSoftwareHongKongPage'));
const HrSoftwareTaiwanPage = React.lazy(() => import('../pages/seo/HrSoftwareTaiwanPage'));
const HrSoftwareGhanaPage = React.lazy(() => import('../pages/seo/HrSoftwareGhanaPage'));
const HrSoftwarePakistanPage = React.lazy(() => import('../pages/seo/HrSoftwarePakistanPage'));
const HrSoftwareEthiopiaPage = React.lazy(() => import('../pages/seo/HrSoftwareEthiopiaPage'));
const HrSoftwareRussiaPage = React.lazy(() => import('../pages/seo/HrSoftwareRussiaPage'));
const HrSoftwarePeruPage2 = React.lazy(() => import('../pages/seo/HrSoftwarePeruPage2'));
const HrSoftwareBoliviaPage = React.lazy(() => import('../pages/seo/HrSoftwareBoliviaPage'));
const HrSoftwareParaguayPage = React.lazy(() => import('../pages/seo/HrSoftwareParaguayPage'));
const HrSoftwareUruguayPage = React.lazy(() => import('../pages/seo/HrSoftwareUruguayPage'));
const HrSoftwareMozambiquePage = React.lazy(() => import('../pages/seo/HrSoftwareMozambiquePage'));
const HrSoftwareCameroonPage = React.lazy(() => import('../pages/seo/HrSoftwareCameroonPage'));
const HrSoftwareIvoryCoastPage = React.lazy(() => import('../pages/seo/HrSoftwareIvoryCoastPage'));
const HrSoftwareTunisiaPage = React.lazy(() => import('../pages/seo/HrSoftwareTunisiaPage'));
const HrSoftwareAlgeriaPage = React.lazy(() => import('../pages/seo/HrSoftwareAlgeriaPage'));
const HrSoftwareLibyaPage = React.lazy(() => import('../pages/seo/HrSoftwareLibyaPage'));
const HrSoftwareLebanonPage = React.lazy(() => import('../pages/seo/HrSoftwareLebanonPage'));
const HrSoftwareAfghanistanPage = React.lazy(() => import('../pages/seo/HrSoftwareAfghanistanPage'));
const HrSoftwareLaosPage = React.lazy(() => import('../pages/seo/HrSoftwareLaosPage'));
const HrSoftwareUzbekistanPage = React.lazy(() => import('../pages/seo/HrSoftwareUzbekistanPage'));
const HrSoftwareAtlantaPage = React.lazy(() => import('../pages/seo/HrSoftwareAtlantaPage'));
const HrSoftwareMiamiPage = React.lazy(() => import('../pages/seo/HrSoftwareMiamiPage'));
const HrSoftwareSeattlePage = React.lazy(() => import('../pages/seo/HrSoftwareSeattlePage'));
const HrSoftwareBostonPage = React.lazy(() => import('../pages/seo/HrSoftwareBostonPage'));
const HrSoftwarePhoenixPage = React.lazy(() => import('../pages/seo/HrSoftwarePhoenixPage'));
const HrSoftwareDarEsSalaamPage = React.lazy(() => import('../pages/seo/HrSoftwareDarEsSalaamPage'));
const HrSoftwareAddisAbabaPage = React.lazy(() => import('../pages/seo/HrSoftwareAddisAbabaPage'));
const HrSoftwareKampalaPage = React.lazy(() => import('../pages/seo/HrSoftwareKampalaPage'));
const HrSoftwareOsakaPage = React.lazy(() => import('../pages/seo/HrSoftwareOsakaPage'));
const HrSoftwareHanoiPage = React.lazy(() => import('../pages/seo/HrSoftwareHanoiPage'));
const HrSoftwareIslamabadPage = React.lazy(() => import('../pages/seo/HrSoftwareIslamabadPage'));
const HrSoftwareLahorePage = React.lazy(() => import('../pages/seo/HrSoftwareLahorePage'));
const HrSoftwareCasablancaPage = React.lazy(() => import('../pages/seo/HrSoftwareCasablancaPage'));
const HrSoftwareAbujaPage = React.lazy(() => import('../pages/seo/HrSoftwareAbujaPage'));
const HrSoftwareBusanPage = React.lazy(() => import('../pages/seo/HrSoftwareBusanPage'));
const HrSoftwareBrisbanePage = React.lazy(() => import('../pages/seo/HrSoftwareBrisbanePage'));
const HrSoftwareBrisbaneQldPage = React.lazy(() => import('../pages/seo/HrSoftwareBrisbaneQldPage'));
const HrSoftwareAucklandPage = React.lazy(() => import('../pages/seo/HrSoftwareAucklandPage'));
const HrSoftwareCalgaryPage = React.lazy(() => import('../pages/seo/HrSoftwareCalgaryPage'));
const HrSoftwareMexicoCityPage = React.lazy(() => import('../pages/seo/HrSoftwareMexicoCityPage'));
const HrSoftwareMilanPage = React.lazy(() => import('../pages/seo/HrSoftwareMilanPage'));
const HrSoftwareViennaPage = React.lazy(() => import('../pages/seo/HrSoftwareViennaPage'));
const HrSoftwareHelsinkiPage = React.lazy(() => import('../pages/seo/HrSoftwareHelsinkiPage'));
const HrSoftwareAthensPage = React.lazy(() => import('../pages/seo/HrSoftwareAthensPage'));
const HrSoftwareAustriaPage = React.lazy(() => import('../pages/seo/HrSoftwareAustriaPage'));
const HrSoftwareFinlandPage = React.lazy(() => import('../pages/seo/HrSoftwareFinlandPage'));
const HrSoftwareGreecePage = React.lazy(() => import('../pages/seo/HrSoftwareGreecePage'));
const HrSoftwareIsraelPage = React.lazy(() => import('../pages/seo/HrSoftwareIsraelPage'));
const HrSoftwarePeruPage = React.lazy(() => import('../pages/seo/HrSoftwarePeruPage'));
const HrSoftwareRomaniaPage = React.lazy(() => import('../pages/seo/HrSoftwareRomaniaPage'));
const HrSoftwareHungaryPage = React.lazy(() => import('../pages/seo/HrSoftwareHungaryPage'));
const HrSoftwareSerbiaPage = React.lazy(() => import('../pages/seo/HrSoftwareSerbiaPage'));
const HrSoftwareUkrainePage = React.lazy(() => import('../pages/seo/HrSoftwareUkrainePage'));
const HrSoftwareEcuadorPage = React.lazy(() => import('../pages/seo/HrSoftwareEcuadorPage'));
const HrSoftwareZimbabwePage = React.lazy(() => import('../pages/seo/HrSoftwareZimbabwePage'));
const HrSoftwareZambiaPage = React.lazy(() => import('../pages/seo/HrSoftwareZambiaPage'));
const HrSoftwareSenegalPage = React.lazy(() => import('../pages/seo/HrSoftwareSenegalPage'));
const HrSoftwareCambodiaPage = React.lazy(() => import('../pages/seo/HrSoftwareCambodiaPage'));
const HrSoftwareMyanmarPage = React.lazy(() => import('../pages/seo/HrSoftwareMyanmarPage'));
const HrSoftwareKazakhstanPage = React.lazy(() => import('../pages/seo/HrSoftwareKazakhstanPage'));
const HrSoftwareAngolaPage = React.lazy(() => import('../pages/seo/HrSoftwareAngolaPage'));
const HrSoftwareVenezuelaPage = React.lazy(() => import('../pages/seo/HrSoftwareVenezuelaPage'));
const HrSoftwareParisPage = React.lazy(() => import('../pages/seo/HrSoftwareParisPage'));
const HrSoftwareChicagoPage = React.lazy(() => import('../pages/seo/HrSoftwareChicagoPage'));
const HrSoftwareHoustonPage = React.lazy(() => import('../pages/seo/HrSoftwareHoustonPage'));
const HrSoftwareDallasPage = React.lazy(() => import('../pages/seo/HrSoftwareDallasPage'));
const HrSoftwareSanFranciscoPage = React.lazy(() => import('../pages/seo/HrSoftwareSanFranciscoPage'));
const HrSoftwareDublinPage = React.lazy(() => import('../pages/seo/HrSoftwareDublinPage'));
const HrSoftwareRomePage = React.lazy(() => import('../pages/seo/HrSoftwareRomePage'));
const HrSoftwareMadridPage = React.lazy(() => import('../pages/seo/HrSoftwareMadridPage'));
const HrSoftwareBarcelonaPage = React.lazy(() => import('../pages/seo/HrSoftwareBarcelonaPage'));
const HrSoftwareStockholmPage = React.lazy(() => import('../pages/seo/HrSoftwareStockholmPage'));
const HrSoftwareMunichPage = React.lazy(() => import('../pages/seo/HrSoftwareMunichPage'));
const HrSoftwareZurichPage = React.lazy(() => import('../pages/seo/HrSoftwareZurichPage'));
const HrSoftwareBrusselsPage = React.lazy(() => import('../pages/seo/HrSoftwareBrusselsPage'));
const HrSoftwareWarsawPage = React.lazy(() => import('../pages/seo/HrSoftwareWarsawPage'));
const HrSoftwarePraguePage = React.lazy(() => import('../pages/seo/HrSoftwarePraguePage'));
const HrSoftwareBuenosAiresPage = React.lazy(() => import('../pages/seo/HrSoftwareBuenosAiresPage'));
const HrSoftwareSantiagoPage = React.lazy(() => import('../pages/seo/HrSoftwareSantiagoPage'));
const HrSoftwareBogotaPage = React.lazy(() => import('../pages/seo/HrSoftwareBogotaPage'));
const HrSoftwareCapeTownPage = React.lazy(() => import('../pages/seo/HrSoftwareCapeTownPage'));
const HrSoftwareTaipeiPage = React.lazy(() => import('../pages/seo/HrSoftwareTaipeiPage'));
const HrSoftwareHoChiMinhCityPage = React.lazy(() => import('../pages/seo/HrSoftwareHoChiMinhCityPage'));
const HrSoftwareAccraPage = React.lazy(() => import('../pages/seo/HrSoftwareAccraPage'));
const HrSoftwareKarachiPage = React.lazy(() => import('../pages/seo/HrSoftwareKarachiPage'));
const HrSoftwareDhakaPage = React.lazy(() => import('../pages/seo/HrSoftwareDhakaPage'));
const HrSoftwareColomboPage = React.lazy(() => import('../pages/seo/HrSoftwareColomboPage'));
const HrSoftwareArgentinaPage = React.lazy(() => import('../pages/seo/HrSoftwareArgentinaPage'));
const HrSoftwareColombiaPage = React.lazy(() => import('../pages/seo/HrSoftwareColombiaPage'));
const HrSoftwareFrancePage = React.lazy(() => import('../pages/seo/HrSoftwareFrancePage'));
const HrSoftwareSpainPage = React.lazy(() => import('../pages/seo/HrSoftwareSpainPage'));
const HrSoftwareItalyPage = React.lazy(() => import('../pages/seo/HrSoftwareItalyPage'));
const HrSoftwareSwedenPage = React.lazy(() => import('../pages/seo/HrSoftwareSwedenPage'));
const HrSoftwareSouthKoreaPage = React.lazy(() => import('../pages/seo/HrSoftwareSouthKoreaPage'));
const HrSoftwareMoroccoPage = React.lazy(() => import('../pages/seo/HrSoftwareMoroccoPage'));
const HrSoftwareTanzaniaPage = React.lazy(() => import('../pages/seo/HrSoftwareTanzaniaPage'));
const HrSoftwareChilePage = React.lazy(() => import('../pages/seo/HrSoftwareChilePage'));
const HrSoftwareNorwayPage = React.lazy(() => import('../pages/seo/HrSoftwareNorwayPage'));
const HrSoftwareDenmarkPage = React.lazy(() => import('../pages/seo/HrSoftwareDenmarkPage'));
const HrSoftwareSwitzerlandPage = React.lazy(() => import('../pages/seo/HrSoftwareSwitzerlandPage'));
const HrSoftwarePortugalPage = React.lazy(() => import('../pages/seo/HrSoftwarePortugalPage'));
const HrSoftwareBelgiumPage = React.lazy(() => import('../pages/seo/HrSoftwareBelgiumPage'));
const HrSoftwarePolandPage = React.lazy(() => import('../pages/seo/HrSoftwarePolandPage'));
const HrSoftwareCzechRepublicPage = React.lazy(() => import('../pages/seo/HrSoftwareCzechRepublicPage'));
const HrSoftwareUgandaPage = React.lazy(() => import('../pages/seo/HrSoftwareUgandaPage'));
const HrSoftwareLondonPage = React.lazy(() => import('../pages/seo/HrSoftwareLondonPage'));
const HrSoftwareManchesterPage = React.lazy(() => import('../pages/seo/HrSoftwareManchesterPage'));
const HrSoftwareSydneyPage = React.lazy(() => import('../pages/seo/HrSoftwareSydneyPage'));
const HrSoftwareMelbournePage = React.lazy(() => import('../pages/seo/HrSoftwareMelbournePage'));
const HrSoftwareTorontoPage = React.lazy(() => import('../pages/seo/HrSoftwareTorontoPage'));
const HrSoftwareVancouverPage = React.lazy(() => import('../pages/seo/HrSoftwareVancouverPage'));
const HrSoftwareNewYorkPage = React.lazy(() => import('../pages/seo/HrSoftwareNewYorkPage'));
const HrSoftwareLosAngelesPage = React.lazy(() => import('../pages/seo/HrSoftwareLosAngelesPage'));
const HrSoftwareAbuDhabiPage = React.lazy(() => import('../pages/seo/HrSoftwareAbuDhabiPage'));
const HrSoftwareRiyadhPage = React.lazy(() => import('../pages/seo/HrSoftwareRiyadhPage'));
const HrSoftwareTokyoPage = React.lazy(() => import('../pages/seo/HrSoftwareTokyoPage'));
const HrSoftwareSeoulPage = React.lazy(() => import('../pages/seo/HrSoftwareSeoulPage'));
const HrSoftwareKualaLumpurPage = React.lazy(() => import('../pages/seo/HrSoftwareKualaLumpurPage'));
const HrSoftwareManilaPage = React.lazy(() => import('../pages/seo/HrSoftwareManilaPage'));
const HrSoftwareJakartaPage = React.lazy(() => import('../pages/seo/HrSoftwareJakartaPage'));
const HrSoftwareBangkokPage = React.lazy(() => import('../pages/seo/HrSoftwareBangkokPage'));
const HrSoftwareLagosPage = React.lazy(() => import('../pages/seo/HrSoftwareLagosPage'));
const HrSoftwareNairobiPage = React.lazy(() => import('../pages/seo/HrSoftwareNairobiPage'));
const HrSoftwareIstanbulPage = React.lazy(() => import('../pages/seo/HrSoftwareIstanbulPage'));
const HrSoftwareJohannesburgPage = React.lazy(() => import('../pages/seo/HrSoftwareJohannesburgPage'));
const HrSoftwareCairoPage = React.lazy(() => import('../pages/seo/HrSoftwareCairoPage'));
const HrSoftwareBerlinPage = React.lazy(() => import('../pages/seo/HrSoftwareBerlinPage'));
const HrSoftwareAmsterdamPage = React.lazy(() => import('../pages/seo/HrSoftwareAmsterdamPage'));
const HrSoftwareSaoPauloPage = React.lazy(() => import('../pages/seo/HrSoftwareSaoPauloPage'));
const HrSoftwareKolkataPage = React.lazy(() => import('../pages/seo/HrSoftwareKolkataPage'));
const HrSoftwarePatnaPage = React.lazy(() => import('../pages/seo/HrSoftwarePatnaPage'));
const HrSoftwareBhubaneswarPage = React.lazy(() => import('../pages/seo/HrSoftwareBhubaneswarPage'));
const HrSoftwareGuwahatiPage = React.lazy(() => import('../pages/seo/HrSoftwareGuwahatiPage'));
const HrSoftwareJodhpurPage = React.lazy(() => import('../pages/seo/HrSoftwareJodhpurPage'));
const HrSoftwareNashikPage = React.lazy(() => import('../pages/seo/HrSoftwareNashikPage'));
const HrSoftwareMaduraiPage = React.lazy(() => import('../pages/seo/HrSoftwareMaduraiPage'));
const HrSoftwareThiruvananthapuramPage = React.lazy(() => import('../pages/seo/HrSoftwareThiruvananthapuramPage'));
const HrSoftwareRaipurPage = React.lazy(() => import('../pages/seo/HrSoftwareRaipurPage'));
const HrSoftwareDehradunPage = React.lazy(() => import('../pages/seo/HrSoftwareDehradunPage'));
const HrSoftwareMysurePage = React.lazy(() => import('../pages/seo/HrSoftwareMysurePage'));
const HrSoftwareMangalorePage = React.lazy(() => import('../pages/seo/HrSoftwareMangalorePage'));
const HrSoftwareVijayawadaPage = React.lazy(() => import('../pages/seo/HrSoftwareVijayawadaPage'));
const HrSoftwareTiruchirapalliPage = React.lazy(() => import('../pages/seo/HrSoftwareTiruchirapalliPage'));
const HrSoftwareSalemPage = React.lazy(() => import('../pages/seo/HrSoftwareSalemPage'));
const HrSoftwareRajkotPage = React.lazy(() => import('../pages/seo/HrSoftwareRajkotPage'));
const HrSoftwareAmritsarPage = React.lazy(() => import('../pages/seo/HrSoftwareAmritsarPage'));
const HrSoftwareUdaipurPage = React.lazy(() => import('../pages/seo/HrSoftwareUdaipurPage'));
const HrSoftwareHubliPage = React.lazy(() => import('../pages/seo/HrSoftwareHubliPage'));
const HrSoftwareAurangabadPage = React.lazy(() => import('../pages/seo/HrSoftwareAurangabadPage'));
const HrSoftwareRanchiPage = React.lazy(() => import('../pages/seo/HrSoftwareRanchiPage'));
const HrSoftwareNaviMumbaiPage = React.lazy(() => import('../pages/seo/HrSoftwareNaviMumbaiPage'));
const HrSoftwareThanePage = React.lazy(() => import('../pages/seo/HrSoftwareThanePage'));
const HrSoftwareSiliguriPage = React.lazy(() => import('../pages/seo/HrSoftwareSiliguriPage'));
const HrSoftwareWestBengalPage = React.lazy(() => import('../pages/seo/HrSoftwareWestBengalPage'));
const HrSoftwareBiharPage = React.lazy(() => import('../pages/seo/HrSoftwareBiharPage'));
const HrSoftwareOdishaPage = React.lazy(() => import('../pages/seo/HrSoftwareOdishaPage'));
const HrSoftwareAssamPage = React.lazy(() => import('../pages/seo/HrSoftwareAssamPage'));
const HrSoftwareJharkhandPage = React.lazy(() => import('../pages/seo/HrSoftwareJharkhandPage'));
const HrSoftwareChhattisgarhPage = React.lazy(() => import('../pages/seo/HrSoftwareChhattisgarhPage'));
const HrSoftwareAndhraPradeshPage = React.lazy(() => import('../pages/seo/HrSoftwareAndhraPradeshPage'));
const HrSoftwareMadhyaPradeshPage = React.lazy(() => import('../pages/seo/HrSoftwareMadhyaPradeshPage'));
const HrSoftwareGoaPage = React.lazy(() => import('../pages/seo/HrSoftwareGoaPage'));
const HrSoftwareHimachalPradeshPage = React.lazy(() => import('../pages/seo/HrSoftwareHimachalPradeshPage'));
const HrSoftwareUttarakhandPage = React.lazy(() => import('../pages/seo/HrSoftwareUttarakhandPage'));
const HrSoftwareTripuraPage = React.lazy(() => import('../pages/seo/HrSoftwareTripuraPage'));
const HrSoftwareGwaliorPage = React.lazy(() => import('../pages/seo/HrSoftwareGwaliorPage'));
const HrSoftwareJabalpurPage = React.lazy(() => import('../pages/seo/HrSoftwareJabalpurPage'));
const HrSoftwareKolhapurPage = React.lazy(() => import('../pages/seo/HrSoftwareKolhapurPage'));
const HrSoftwarePimpriChinchwadPage = React.lazy(() => import('../pages/seo/HrSoftwarePimpriChinchwadPage'));
const HrSoftwareShimlaPage = React.lazy(() => import('../pages/seo/HrSoftwareShimlaPage'));
const HrSoftwareJammuPage = React.lazy(() => import('../pages/seo/HrSoftwareJammuPage'));
const HrSoftwareDurgapurPage = React.lazy(() => import('../pages/seo/HrSoftwareDurgapurPage'));
const HrSoftwareSanJosePage = React.lazy(() => import('../pages/seo/HrSoftwareSanJosePage'));
const HrSoftwareAustinPage = React.lazy(() => import('../pages/seo/HrSoftwareAustinPage'));
const HrSoftwareDenverPage = React.lazy(() => import('../pages/seo/HrSoftwareDenverPage'));
const HrSoftwarePortlandPage = React.lazy(() => import('../pages/seo/HrSoftwarePortlandPage'));
const HrSoftwareNashvillePage = React.lazy(() => import('../pages/seo/HrSoftwareNashvillePage'));
const HrSoftwareCharlottePage = React.lazy(() => import('../pages/seo/HrSoftwareCharlottePage'));
const HrSoftwareRaleighPage = React.lazy(() => import('../pages/seo/HrSoftwareRaleighPage'));
const HrSoftwareMinneapolisPage = React.lazy(() => import('../pages/seo/HrSoftwareMinneapolisPage'));
const HrSoftwareNewJerseyPage = React.lazy(() => import('../pages/seo/HrSoftwareNewJerseyPage'));
const HrSoftwareWashingtonDcPage = React.lazy(() => import('../pages/seo/HrSoftwareWashingtonDcPage'));
const HrSoftwareTampaPage = React.lazy(() => import('../pages/seo/HrSoftwareTampaPage'));
const HrSoftwareSanDiegoPage = React.lazy(() => import('../pages/seo/HrSoftwareSanDiegoPage'));
const HrSoftwareLasVegasPage = React.lazy(() => import('../pages/seo/HrSoftwareLasVegasPage'));
const HrSoftwareSacramentoPage = React.lazy(() => import('../pages/seo/HrSoftwareSacramentoPage'));
const HrSoftwarePittsburghPage = React.lazy(() => import('../pages/seo/HrSoftwarePittsburghPage'));
const HrSoftwareColumbusPage = React.lazy(() => import('../pages/seo/HrSoftwareColumbusPage'));
const HrSoftwareDetroitPage = React.lazy(() => import('../pages/seo/HrSoftwareDetroitPage'));
const HrSoftwareKansasCityPage = React.lazy(() => import('../pages/seo/HrSoftwareKansasCityPage'));
const HrSoftwareIndianapolisPage = React.lazy(() => import('../pages/seo/HrSoftwareIndianapolisPage'));
const HrSoftwareOrlandoPage = React.lazy(() => import('../pages/seo/HrSoftwareOrlandoPage'));
const AiChatbotForHrPage = React.lazy(() => import('../pages/seo/AiChatbotForHrPage'));
const ConversationalHrSoftwarePage = React.lazy(() => import('../pages/seo/ConversationalHrSoftwarePage'));
const AiHrChatbotPage = React.lazy(() => import('../pages/seo/AiHrChatbotPage'));
const NaturalLanguageHrQueryPage = React.lazy(() => import('../pages/seo/NaturalLanguageHrQueryPage'));
const AiEmployeeSelfServicePage = React.lazy(() => import('../pages/seo/AiEmployeeSelfServicePage'));
const AiPayrollSoftwarePage = React.lazy(() => import('../pages/seo/AiPayrollSoftwarePage'));
const AiAttendanceSystemPage = React.lazy(() => import('../pages/seo/AiAttendanceSystemPage'));
const AiLeaveManagementPage = React.lazy(() => import('../pages/seo/AiLeaveManagementPage'));
const AiHrAutomationPage = React.lazy(() => import('../pages/seo/AiHrAutomationPage'));
const AiPayrollIndiaPage = React.lazy(() => import('../pages/seo/AiPayrollIndiaPage'));
const AiCrmSoftwarePage = React.lazy(() => import('../pages/seo/AiCrmSoftwarePage'));
const AiSalesAssistantPage = React.lazy(() => import('../pages/seo/AiSalesAssistantPage'));
const AiLeadManagementPage = React.lazy(() => import('../pages/seo/AiLeadManagementPage'));
const AiMeetingAssistantPage = React.lazy(() => import('../pages/seo/AiMeetingAssistantPage'));
const AiSalesCrmIndiaPage = React.lazy(() => import('../pages/seo/AiSalesCrmIndiaPage'));
const AiTaskManagementPage = React.lazy(() => import('../pages/seo/AiTaskManagementPage'));
const AiProjectManagementPage = React.lazy(() => import('../pages/seo/AiProjectManagementPage'));
const AiBusinessManagementSoftwarePage = React.lazy(() => import('../pages/seo/AiBusinessManagementSoftwarePage'));
const AiErpSoftwarePage = React.lazy(() => import('../pages/seo/AiErpSoftwarePage'));
const AiOkrSoftwarePage = React.lazy(() => import('../pages/seo/AiOkrSoftwarePage'));
const AiHrAnalyticsPage = React.lazy(() => import('../pages/seo/AiHrAnalyticsPage'));
const AiHrReportingPage = React.lazy(() => import('../pages/seo/AiHrReportingPage'));
const AiPerformanceManagementPage = React.lazy(() => import('../pages/seo/AiPerformanceManagementPage'));
const AiWorkforceAnalyticsPage = React.lazy(() => import('../pages/seo/AiWorkforceAnalyticsPage'));
const AiReportGeneratorPage = React.lazy(() => import('../pages/seo/AiReportGeneratorPage'));
const AiHrForSmallBusinessPage = React.lazy(() => import('../pages/seo/AiHrForSmallBusinessPage'));
const AiHrForStartupsPage = React.lazy(() => import('../pages/seo/AiHrForStartupsPage'));
const AiHrForEnterprisesPage = React.lazy(() => import('../pages/seo/AiHrForEnterprisesPage'));
const AiHrForAgenciesPage = React.lazy(() => import('../pages/seo/AiHrForAgenciesPage'));
const GenerativeAiForHrPage = React.lazy(() => import('../pages/seo/GenerativeAiForHrPage'));
const AiHrSoftwareDubaiPage = React.lazy(() => import('../pages/seo/AiHrSoftwareDubaiPage'));
const AiHrSoftwareUsaPage = React.lazy(() => import('../pages/seo/AiHrSoftwareUsaPage'));
const AiHrSoftwareUkPage = React.lazy(() => import('../pages/seo/AiHrSoftwareUkPage'));
const AiCopilotForBusinessPage = React.lazy(() => import('../pages/seo/AiCopilotForBusinessPage'));
const AiHrHelpdeskPage = React.lazy(() => import('../pages/seo/AiHrHelpdeskPage'));

// Blog pages — public, self-contained layout
const BlogIndex = React.lazy(() => import('../pages/blog/BlogIndex'));
const BlogPost = React.lazy(() => import('../pages/blog/BlogPost'));

// Get Access page — public lead-capture form (replaces old employee RegisterPage)
const GetAccessPage = React.lazy(() => import('../pages/GetAccessPage'));
const GetHrPortalAccessPage = React.lazy(() => import('../pages/GetHrPortalAccessPage'));

// Admin website-traffic analytics
const SiteAnalytics = React.lazy(() => import('../pages/SiteAnalytics'));
const CalculatorReport = React.lazy(() => import('../pages/CalculatorReport'));
const HrDocuments = React.lazy(() => import('../pages/HrDocuments'));
const LoginAttempts = React.lazy(() => import('../pages/LoginAttempts'));
const AiProvidersPage = React.lazy(() => import('../pages/AiProvidersPage'));
const PremiumPdfRevenuePage = React.lazy(() => import('../pages/PremiumPdfRevenuePage'));

const AppRouter = () => {
  return (
    <Suspense fallback={<div className="flex justify-center items-center h-screen"><Spinner /></div>}>
      <Routes>
      {/* Public Routes */}
      <Route path="/" element={<MainLayout><HomePage /></MainLayout>} />
      <Route path="/login" element={<MainLayout><LoginPage /></MainLayout>} />
      {/* /register was the old employee self-registration page — HR now handles onboarding. */}
      <Route path="/register" element={<Navigate to="/get-hr-portal-access" replace />} />
      {/* Single canonical request-access page. /get-access is consolidated into it
          (also a real 301 in vercel.json) to avoid duplicate-content + split equity. */}
      <Route path="/get-access" element={<Navigate to="/get-hr-portal-access" replace />} />
      <Route path="/get-hr-portal-access" element={<GetHrPortalAccessPage />} />
      <Route path="/partner-signup" element={<PartnerSignupPage />} />
      {/* Admin-only registration pages. The backend also enforces this:
          - /register-hr requires an authenticated admin token
          - /register-admin requires an existing admin (or auto-allows the
            very first admin if the DB has zero admins, for fresh installs)
          These were previously public, which let anyone self-grant HR/admin
          access — a critical security hole. */}
      <Route
        path="/register-hr"
        element={
          <ProtectedRoute roles={['admin']}>
            <RegisterHRPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/register-admin"
        element={
          <ProtectedRoute roles={['admin']}>
            <RegisterAdminPage />
          </ProtectedRoute>
        }
      />
      <Route path="/forgot-password" element={<MainLayout><ForgotPasswordPage /></MainLayout>} />
      <Route path="/privacy-policy" element={<MainLayout><PrivacyPolicyPage /></MainLayout>} />

      {/* SEO Landing Pages — public, use their own SeoLandingLayout (no MainLayout wrapper) */}
      <Route path="/greythr-vs-keka-vs-avani" element={<GreytHRvsKekavsAvani />} />
      <Route path="/payroll-compliance-software-india" element={<PayrollComplianceHub />} />
      <Route path="/gst-invoice-software-india" element={<GstInvoicingSoftware />} />
      <Route path="/shift-management-software" element={<ShiftManagementSoftware />} />

      {/* 10 new Email CRM SEO Routes */}
      <Route path="/bulk-email-sender" element={<BulkEmailSenderPage />} />
      <Route path="/email-crm-software" element={<EmailCrmSoftwarePage />} />
      <Route path="/email-campaign-software" element={<EmailCampaignSoftwarePage />} />
      <Route path="/bulk-email-without-blocking" element={<BulkEmailWithoutBlockingPage />} />
      <Route path="/email-marketing-software-india" element={<EmailMarketingSoftwareIndiaPage />} />
      <Route path="/cold-email-software" element={<ColdEmailSoftwarePage />} />
      <Route path="/bulk-email-analytics" element={<BulkEmailAnalyticsPage />} />
      <Route path="/newsletter-creator" element={<NewsletterCreatorPage />} />
      <Route path="/email-deliverability-software" element={<EmailDeliverabilitySoftwarePage />} />
      <Route path="/mass-email-sender-india" element={<MassEmailSenderIndiaPage />} />

      <Route path="/hr-portal" element={<HrPortalPage />} />
      <Route path="/hrms-software-india" element={<HrmsSoftwarePage />} />
      <Route path="/payroll-software-india" element={<PayrollSoftwarePage />} />
      <Route path="/attendance-management-system" element={<AttendancePage />} />
      <Route path="/leave-management-software" element={<LeavePage />} />
      <Route path="/employee-management-software" element={<EmployeeManagementPage />} />
      <Route path="/employee-portal" element={<EmployeePortalPage />} />
      <Route path="/crm-software-india" element={<CrmPage />} />
      <Route path="/crm-for-small-business" element={<CrmSmallBusinessPage />} />
      <Route path="/workforce-management-software" element={<WorkforcePage />} />
      <Route path="/project-management-software" element={<ProjectManagementPage />} />
      <Route path="/business-operating-system" element={<BusinessOsPage />} />
      <Route path="/business-management-software-india" element={<BusinessManagementPage />} />
      <Route path="/keka-alternative" element={<KekaAlternativePage />} />
      <Route path="/greythr-alternative" element={<GreythrAlternativePage />} />
      <Route path="/darwinbox-alternative" element={<DarwinboxAlternativePage />} />
      <Route path="/zoho-people-alternative" element={<ZohoPeoplePage />} />
      <Route path="/sumhr-alternative" element={<SumhrAlternativePage />} />
      <Route path="/freshteam-alternative" element={<FreshteamAlternativePage />} />
      <Route path="/salarybox-alternative" element={<SalaryBoxAlternativePage />} />
      <Route path="/petpooja-alternative" element={<PetpoojaAlternativePage />} />
      <Route path="/kredily-alternative" element={<KredilyAlternativePage />} />
      <Route path="/vyapar-alternative" element={<VyaparAlternativePage />} />
      <Route path="/hr-software-manufacturing" element={<ManufacturingPage />} />
      <Route path="/hr-software-it-company" element={<ItCompanyPage />} />
      <Route path="/free-hrms-india" element={<FreeHrmsPage />} />
      <Route path="/salary-slip-software" element={<SalarySlipPage />} />
      <Route path="/hr-software-mumbai" element={<MumbaiPage />} />
      <Route path="/hr-software-bangalore" element={<BangalorePage />} />
      <Route path="/hr-software-delhi" element={<DelhiPage />} />
      <Route path="/hr-software-small-business" element={<SmallBusinessPage />} />
      <Route path="/hr-software-startups" element={<StartupsPage />} />
      <Route path="/performance-management-software" element={<PerformanceManagementPage />} />
      {/* City landing pages */}
      <Route path="/hr-software-noida" element={<NoidaPage />} />
      <Route path="/hr-software-gurgaon" element={<GurgaonPage />} />
      <Route path="/hr-software-pune" element={<PunePage />} />
      <Route path="/hr-software-hyderabad" element={<HyderabadPage />} />
      <Route path="/hr-software-chennai" element={<ChennaiPage />} />
      <Route path="/hr-software-ahmedabad" element={<AhmedabadPage />} />
      <Route path="/hr-software-rohtak" element={<RohtakPage />} />
      <Route path="/hr-software-lucknow" element={<HrSoftwareLucknowPage />} />
      <Route path="/hr-software-agra" element={<HrSoftwareAgraPage />} />
      <Route path="/hr-software-meerut" element={<HrSoftwareMeerutPage />} />
      <Route path="/hr-software-kanpur" element={<HrSoftwareKanpurPage />} />
      <Route path="/hr-software-varanasi" element={<HrSoftwareVaranasiPage />} />
      <Route path="/hr-software-ghaziabad" element={<HrSoftwareGhaziabadPage />} />
      <Route path="/hr-software-greater-noida" element={<HrSoftwareGreaterNoidaPage />} />
      <Route path="/hr-software-allahabad" element={<HrSoftwareAllahabadPage />} />
      <Route path="/hr-software-jhajjar" element={<HrSoftwareJhajjarPage />} />
      <Route path="/hr-software-rewari" element={<HrSoftwareRewariPage />} />
      <Route path="/hr-software-bahadurgarh" element={<HrSoftwareBahadurgarhPage />} />
      <Route path="/hr-software-bhiwani" element={<HrSoftwareBhiwaniPage />} />
      <Route path="/hr-software-sirsa" element={<HrSoftwareSirsaPage />} />
      <Route path="/hr-software-jind" element={<HrSoftwareJindPage />} />
      <Route path="/hr-software-kurukshetra" element={<HrSoftwareKurukshetraPage />} />
      <Route path="/hr-software-panchkula" element={<HrSoftwarePanchkulaPage />} />
      <Route path="/hr-software-yamunanagar" element={<HrSoftwareYamunanagarPage />} />
      <Route path="/hr-software-palwal" element={<HrSoftwarePalwalPage />} />
      <Route path="/hr-software-manesar" element={<HrSoftwareMaesarPage />} />
      <Route path="/hr-software-faridabad-ncr" element={<HrSoftwareFaridabadNcrPage />} />
      <Route path="/hr-software-delhi-ncr" element={<HrSoftwareDelhiNcrPage />} />
      <Route path="/hr-software-west-delhi" element={<HrSoftwareWestDelhiPage />} />
      <Route path="/hr-software-south-delhi" element={<HrSoftwareSouthDelhiPage />} />
      <Route path="/hr-software-east-delhi" element={<HrSoftwareEastDelhiPage />} />
      {/* State landing pages */}
      <Route path="/hr-software-haryana" element={<HaryanaPage />} />
      <Route path="/hr-software-kerala" element={<KeralaPage />} />
      <Route path="/hr-software-karnataka" element={<KarnatakaPage />} />
      <Route path="/hr-software-maharashtra" element={<MaharashtraPage />} />
      <Route path="/hr-software-tamil-nadu" element={<TamilNaduPage />} />

      {/* About */}
      <Route path="/about" element={<AboutPage />} />

      {/* New city pages — North Haryana */}
      <Route path="/hr-software-faridabad" element={<FaridabadPage />} />
      <Route path="/hr-software-panipat" element={<PanipatPage />} />
      <Route path="/hr-software-karnal" element={<KarnalPage />} />
      <Route path="/hr-software-sonipat" element={<SonipatPage />} />
      <Route path="/hr-software-hisar" element={<HisarPage />} />
      <Route path="/hr-software-ambala" element={<AmbalaPage />} />
      <Route path="/hr-software-panchkula" element={<PanchkulaPage />} />
      <Route path="/hr-software-yamunanagar" element={<YamunanagarPage />} />

      {/* New city pages — NCR + North India */}
      <Route path="/hr-software-greater-noida" element={<GreaterNoidaPage />} />
      <Route path="/hr-software-ghaziabad" element={<GhaziabadPage />} />
      <Route path="/hr-software-meerut" element={<MeerutPage />} />
      <Route path="/hr-software-chandigarh" element={<ChandigarhPage />} />
      <Route path="/hr-software-ludhiana" element={<LudhianaPage />} />
      <Route path="/hr-software-jaipur" element={<JaipurPage />} />
      <Route path="/hr-software-indore" element={<IndorePage />} />
      <Route path="/hr-software-coimbatore" element={<CoimbatorePage />} />

      {/* New city pages — South + West India */}
      <Route path="/hr-software-kochi" element={<KochiPage />} />
      <Route path="/hr-software-surat" element={<SuratPage />} />
      <Route path="/hr-software-nagpur" element={<NagpurPage />} />
      <Route path="/hr-software-lucknow" element={<LucknowPage />} />
      <Route path="/hr-software-bhopal" element={<BhopalPage />} />
      <Route path="/hr-software-visakhapatnam" element={<VisakhapatnamPage />} />
      <Route path="/hr-software-vadodara" element={<VadodaraPage />} />

      {/* New keyword pages */}
      <Route path="/hrms" element={<HrmsPage />} />
      <Route path="/hr-software-india" element={<HrSoftwareIndiaPage />} />
      <Route path="/best-hrms-india" element={<BestHrmsIndiaPage />} />
      <Route path="/cloud-hr-software-india" element={<CloudHrPage />} />
      <Route path="/erp-software-india" element={<ErpSoftwarePage />} />
      <Route path="/hr-management-software-india" element={<HrManagementPage />} />
      <Route path="/payroll-management-software" element={<PayrollManagementPage />} />
      <Route path="/biometric-attendance-alternative" element={<BiometricAlternativePage />} />

      {/* New state pages */}
      <Route path="/hr-software-punjab" element={<PunjabPage />} />
      <Route path="/hr-software-rajasthan" element={<RajasthanPage />} />
      <Route path="/hr-software-uttar-pradesh" element={<UtterPradeshPage />} />
      <Route path="/hr-software-telangana" element={<TelanganaPage />} />
      <Route path="/hr-software-gujarat" element={<GujaratPage />} />

      {/* New industry pages */}
      <Route path="/hr-software-retail" element={<RetailPage />} />
      <Route path="/hr-software-healthcare" element={<HealthcarePage />} />
      <Route path="/hr-software-education" element={<EducationPage />} />
      <Route path="/hr-software-construction" element={<ConstructionPage />} />

      {/* New product-category SEO pages (Jun 2026) */}
      <Route path="/expense-management-software-india" element={<ExpenseManagementPage />} />
      <Route path="/asset-management-software-india" element={<AssetManagementPage />} />
      <Route path="/kpi-software-india" element={<KpiSoftwarePage />} />
      <Route path="/okr-software-india" element={<OkrSoftwarePage />} />
      <Route path="/task-management-software" element={<TaskManagementSeoPage />} />
      <Route path="/meeting-management-software" element={<MeetingManagementPage />} />
      <Route path="/gst-invoice-software" element={<GstInvoiceSoftwarePage />} />
      <Route path="/employee-engagement-software" element={<EmployeeEngagementPage />} />
      <Route path="/internal-communication-software" element={<InternalCommunicationPage />} />
      <Route path="/team-management-software" element={<TeamManagementPage />} />
      <Route path="/staff-management-software" element={<StaffManagementPage />} />
      <Route path="/people-management-software" element={<PeopleManagementPage />} />
      <Route path="/employee-onboarding-software" element={<OnboardingSoftwarePage />} />
      <Route path="/attendance-software-india" element={<AttendanceSoftwareIndiaPage />} />
      <Route path="/employee-attendance-software" element={<EmployeeAttendancePage />} />
      <Route path="/online-attendance-system" element={<OnlineAttendancePage />} />
      <Route path="/attendance-tracking-software" element={<AttendanceTrackingPage />} />
      <Route path="/leave-management-system" element={<LeaveManagementSystemPage />} />
      <Route path="/online-leave-management-system" element={<OnlineLeaveManagementPage />} />
      <Route path="/payroll-software-for-small-business" element={<PayrollSmallBusinessPage />} />
      <Route path="/online-payroll-software" element={<OnlinePayrollPage />} />
      <Route path="/recruitment-management-software" element={<RecruitmentSoftwarePage />} />
      <Route path="/applicant-tracking-system-india" element={<AtsSoftwarePage />} />
      <Route path="/sales-pipeline-software" element={<SalesPipelinePage />} />
      <Route path="/contact-management-software" element={<ContactManagementPage />} />
      <Route path="/sales-crm-software" element={<SalesCrmPage />} />
      <Route path="/lead-management-software" element={<LeadManagementPage />} />
      <Route path="/customer-management-software" element={<CustomerManagementPage />} />

      {/* AI keyword SEO pages (Jul 2026) */}
      <Route path="/ai-hr-software" element={<AiHrSoftwarePage />} />
      <Route path="/ai-hrms" element={<AiHrmsPage />} />
      <Route path="/ai-powered-hr-portal" element={<AiPoweredHrPortalPage />} />
      <Route path="/ai-hr-assistant" element={<AiHrAssistantPage />} />
      <Route path="/ai-hr-software-india" element={<AiHrSoftwareIndiaPage />} />

      {/* Finance SEO pages */}
      <Route path="/invoicing-software-india" element={<InvoicingSoftwarePage />} />
      <Route path="/billing-software-india" element={<BillingSoftwarePage />} />
      <Route path="/finance-management-software" element={<FinanceManagementPage />} />
      <Route path="/business-finance-software" element={<BusinessFinancePage />} />

      {/* Free calculator / tool pages */}
      <Route path="/salary-calculator" element={<SalaryCalculatorToolPage />} />
      <Route path="/ctc-calculator" element={<CtcCalculatorPage />} />
      <Route path="/pf-calculator" element={<PfCalculatorPage />} />
      <Route path="/esi-calculator" element={<EsiCalculatorPage />} />
      <Route path="/gratuity-calculator" element={<GratuityCalculatorPage />} />
      <Route path="/salary-slip-generator" element={<SalarySlipGeneratorPage />} />
      <Route path="/rent-receipt-generator" element={<RentReceiptGeneratorPage />} />
      <Route path="/balance-sheet-maker" element={<BalanceSheetMakerPage />} />
      <Route path="/profit-and-loss-statement" element={<ProfitLossStatementPage />} />
      <Route path="/salary-certificate-generator" element={<SalaryCertificateGeneratorPage />} />
      <Route path="/experience-letter-generator" element={<ExperienceLetterGeneratorPage />} />
      <Route path="/full-and-final-settlement-calculator" element={<FullFinalSettlementCalculatorPage />} />
      <Route path="/offer-letter-generator" element={<OfferLetterGeneratorPage />} />
      <Route path="/appointment-letter-generator" element={<AppointmentLetterGeneratorPage />} />
      <Route path="/increment-letter-generator" element={<IncrementLetterGeneratorPage />} />
      <Route path="/internship-certificate-generator" element={<InternshipCertificateGeneratorPage />} />
      <Route path="/free-tools" element={<FreeToolsPage />} />
      <Route path="/notice-period-calculator" element={<NoticePeriodCalculatorPage />} />
      <Route path="/employee-kpi-report-maker" element={<EmployeeKpiReportMakerPage />} />
      <Route path="/leave-calculator" element={<LeaveCalculatorPage />} />
      <Route path="/tds-calculator" element={<TdsCalculatorPage />} />
      <Route path="/hra-calculator" element={<HraCalculatorPage />} />
      <Route path="/income-tax-calculator-india" element={<IncomeTaxCalculatorPage />} />
      <Route path="/ctc-to-in-hand-calculator" element={<CtcToInHandCalculatorPage />} />
      <Route path="/professional-tax-calculator" element={<ProfessionalTaxCalculatorPage />} />
      <Route path="/bonus-calculator-india" element={<BonusCalculatorPage />} />
      <Route path="/leave-encashment-calculator" element={<LeaveEncashmentCalculatorPage />} />
      <Route path="/overtime-calculator" element={<OvertimeCalculatorPage />} />
      <Route path="/minimum-wage-calculator-india" element={<MinimumWageCalculatorPage />} />
      <Route path="/salary-hike-calculator" element={<SalaryHikeCalculatorPage />} />
      <Route path="/loan-emi-calculator" element={<LoanEmiCalculatorPage />} />

      {/* Pricing */}
      <Route path="/pricing" element={<PricingPage />} />

      {/* International SEO pages */}
      <Route path="/hr-software-dubai" element={<HrSoftwareDubaiPage />} />
      <Route path="/hr-software-uae" element={<HrSoftwareUaePage />} />
      <Route path="/hr-software-saudi-arabia" element={<HrSoftwareSaudiPage />} />
      <Route path="/hr-software-qatar" element={<HrSoftwareQatarPage />} />
      <Route path="/hr-software-usa" element={<HrSoftwareUsaPage />} />
      <Route path="/hr-software-uk" element={<HrSoftwareUkPage />} />
      <Route path="/hr-software-australia" element={<HrSoftwareAustraliaPage />} />
      <Route path="/hr-software-singapore" element={<HrSoftwareSingaporePage />} />
      <Route path="/hr-software-canada" element={<HrSoftwareCanadaPage />} />
      <Route path="/hr-software-new-zealand" element={<HrSoftwareNewZealandPage />} />
      <Route path="/hr-software-ireland" element={<HrSoftwareIrelandPage />} />
      <Route path="/hr-software-south-africa" element={<HrSoftwareSouthAfricaPage />} />
      <Route path="/hr-software-nigeria" element={<HrSoftwareNigeriaPage />} />
      <Route path="/hr-software-kenya" element={<HrSoftwareKenyaPage />} />
      <Route path="/hr-software-philippines" element={<HrSoftwarePhilippinesPage />} />
      <Route path="/hr-software-malaysia" element={<HrSoftwareMalaysiaPage />} />
      <Route path="/hr-software-bangladesh" element={<HrSoftwareBangladeshPage />} />
      <Route path="/hr-software-indonesia" element={<HrSoftwareIndonesiaPage />} />
      <Route path="/hr-software-thailand" element={<HrSoftwareThailandPage />} />
      <Route path="/hr-software-vietnam" element={<HrSoftwareVietnamPage />} />
      <Route path="/hr-software-sri-lanka" element={<HrSoftwareSriLankaPage />} />
      <Route path="/hr-software-nepal" element={<HrSoftwareNepalPage />} />
      <Route path="/hr-software-kuwait" element={<HrSoftwareKuwaitPage />} />
      <Route path="/hr-software-bahrain" element={<HrSoftwareBahrainPage />} />
      <Route path="/hr-software-oman" element={<HrSoftwareOmanPage />} />
      <Route path="/hr-software-jordan" element={<HrSoftwareJordanPage />} />
      <Route path="/hr-software-egypt" element={<HrSoftwareEgyptPage />} />
      <Route path="/hr-software-turkey" element={<HrSoftwareTurkeyPage />} />
      <Route path="/hr-software-germany" element={<HrSoftwareGermanyPage />} />
      <Route path="/hr-software-netherlands" element={<HrSoftwareNetherlandsPage />} />
      <Route path="/hr-software-mexico" element={<HrSoftwareMexicoPage />} />
      <Route path="/hr-software-brazil" element={<HrSoftwareBrazilPage />} />
      <Route path="/hr-software-japan" element={<HrSoftwareJapanPage />} />
      <Route path="/hr-software-hong-kong" element={<HrSoftwareHongKongPage />} />
      <Route path="/hr-software-taiwan" element={<HrSoftwareTaiwanPage />} />
      <Route path="/hr-software-ghana" element={<HrSoftwareGhanaPage />} />
      <Route path="/hr-software-pakistan" element={<HrSoftwarePakistanPage />} />
      <Route path="/hr-software-ethiopia" element={<HrSoftwareEthiopiaPage />} />
      <Route path="/hr-software-austria" element={<HrSoftwareAustriaPage />} />
      <Route path="/hr-software-finland" element={<HrSoftwareFinlandPage />} />
      <Route path="/hr-software-greece" element={<HrSoftwareGreecePage />} />
      <Route path="/hr-software-israel" element={<HrSoftwareIsraelPage />} />
      <Route path="/hr-software-peru" element={<HrSoftwarePeruPage />} />
      <Route path="/hr-software-romania" element={<HrSoftwareRomaniaPage />} />
      <Route path="/hr-software-hungary" element={<HrSoftwareHungaryPage />} />
      <Route path="/hr-software-serbia" element={<HrSoftwareSerbiaPage />} />
      <Route path="/hr-software-ukraine" element={<HrSoftwareUkrainePage />} />
      <Route path="/hr-software-ecuador" element={<HrSoftwareEcuadorPage />} />
      <Route path="/hr-software-zimbabwe" element={<HrSoftwareZimbabwePage />} />
      <Route path="/hr-software-zambia" element={<HrSoftwareZambiaPage />} />
      <Route path="/hr-software-senegal" element={<HrSoftwareSenegalPage />} />
      <Route path="/hr-software-cambodia" element={<HrSoftwareCambodiaPage />} />
      <Route path="/hr-software-myanmar" element={<HrSoftwareMyanmarPage />} />
      <Route path="/hr-software-kazakhstan" element={<HrSoftwareKazakhstanPage />} />
      <Route path="/hr-software-angola" element={<HrSoftwareAngolaPage />} />
      <Route path="/hr-software-venezuela" element={<HrSoftwareVenezuelaPage />} />
      <Route path="/hr-software-paris" element={<HrSoftwareParisPage />} />
      <Route path="/hr-software-chicago" element={<HrSoftwareChicagoPage />} />
      <Route path="/hr-software-houston" element={<HrSoftwareHoustonPage />} />
      <Route path="/hr-software-dallas" element={<HrSoftwareDallasPage />} />
      <Route path="/hr-software-san-francisco" element={<HrSoftwareSanFranciscoPage />} />
      <Route path="/hr-software-dublin" element={<HrSoftwareDublinPage />} />
      <Route path="/hr-software-rome" element={<HrSoftwareRomePage />} />
      <Route path="/hr-software-madrid" element={<HrSoftwareMadridPage />} />
      <Route path="/hr-software-barcelona" element={<HrSoftwareBarcelonaPage />} />
      <Route path="/hr-software-stockholm" element={<HrSoftwareStockholmPage />} />
      <Route path="/hr-software-munich" element={<HrSoftwareMunichPage />} />
      <Route path="/hr-software-zurich" element={<HrSoftwareZurichPage />} />
      <Route path="/hr-software-brussels" element={<HrSoftwareBrusselsPage />} />
      <Route path="/hr-software-warsaw" element={<HrSoftwareWarsawPage />} />
      <Route path="/hr-software-prague" element={<HrSoftwarePraguePage />} />
      <Route path="/hr-software-buenos-aires" element={<HrSoftwareBuenosAiresPage />} />
      <Route path="/hr-software-santiago" element={<HrSoftwareSantiagoPage />} />
      <Route path="/hr-software-bogota" element={<HrSoftwareBogotaPage />} />
      <Route path="/hr-software-cape-town" element={<HrSoftwareCapeTownPage />} />
      <Route path="/hr-software-taipei" element={<HrSoftwareTaipeiPage />} />
      <Route path="/hr-software-ho-chi-minh-city" element={<HrSoftwareHoChiMinhCityPage />} />
      <Route path="/hr-software-accra" element={<HrSoftwareAccraPage />} />
      <Route path="/hr-software-karachi" element={<HrSoftwareKarachiPage />} />
      <Route path="/hr-software-dhaka" element={<HrSoftwareDhakaPage />} />
      <Route path="/hr-software-colombo" element={<HrSoftwareColomboPage />} />
      <Route path="/hr-software-argentina" element={<HrSoftwareArgentinaPage />} />
      <Route path="/hr-software-colombia" element={<HrSoftwareColombiaPage />} />
      <Route path="/hr-software-france" element={<HrSoftwareFrancePage />} />
      <Route path="/hr-software-spain" element={<HrSoftwareSpainPage />} />
      <Route path="/hr-software-italy" element={<HrSoftwareItalyPage />} />
      <Route path="/hr-software-sweden" element={<HrSoftwareSwedenPage />} />
      <Route path="/hr-software-south-korea" element={<HrSoftwareSouthKoreaPage />} />
      <Route path="/hr-software-morocco" element={<HrSoftwareMoroccoPage />} />
      <Route path="/hr-software-tanzania" element={<HrSoftwareTanzaniaPage />} />
      <Route path="/hr-software-chile" element={<HrSoftwareChilePage />} />
      <Route path="/hr-software-norway" element={<HrSoftwareNorwayPage />} />
      <Route path="/hr-software-denmark" element={<HrSoftwareDenmarkPage />} />
      <Route path="/hr-software-switzerland" element={<HrSoftwareSwitzerlandPage />} />
      <Route path="/hr-software-portugal" element={<HrSoftwarePortugalPage />} />
      <Route path="/hr-software-belgium" element={<HrSoftwareBelgiumPage />} />
      <Route path="/hr-software-poland" element={<HrSoftwarePolandPage />} />
      <Route path="/hr-software-czech-republic" element={<HrSoftwareCzechRepublicPage />} />
      <Route path="/hr-software-uganda" element={<HrSoftwareUgandaPage />} />
      <Route path="/hr-software-london" element={<HrSoftwareLondonPage />} />
      <Route path="/hr-software-manchester" element={<HrSoftwareManchesterPage />} />
      <Route path="/hr-software-sydney" element={<HrSoftwareSydneyPage />} />
      <Route path="/hr-software-melbourne" element={<HrSoftwareMelbournePage />} />
      <Route path="/hr-software-toronto" element={<HrSoftwareTorontoPage />} />
      <Route path="/hr-software-vancouver" element={<HrSoftwareVancouverPage />} />
      <Route path="/hr-software-new-york" element={<HrSoftwareNewYorkPage />} />
      <Route path="/hr-software-los-angeles" element={<HrSoftwareLosAngelesPage />} />
      <Route path="/hr-software-abu-dhabi" element={<HrSoftwareAbuDhabiPage />} />
      <Route path="/hr-software-riyadh" element={<HrSoftwareRiyadhPage />} />
      <Route path="/hr-software-tokyo" element={<HrSoftwareTokyoPage />} />
      <Route path="/hr-software-seoul" element={<HrSoftwareSeoulPage />} />
      <Route path="/hr-software-kuala-lumpur" element={<HrSoftwareKualaLumpurPage />} />
      <Route path="/hr-software-manila" element={<HrSoftwareManilaPage />} />
      <Route path="/hr-software-jakarta" element={<HrSoftwareJakartaPage />} />
      <Route path="/hr-software-bangkok" element={<HrSoftwareBangkokPage />} />
      <Route path="/hr-software-lagos" element={<HrSoftwareLagosPage />} />
      <Route path="/hr-software-nairobi" element={<HrSoftwareNairobiPage />} />
      <Route path="/hr-software-istanbul" element={<HrSoftwareIstanbulPage />} />
      <Route path="/hr-software-johannesburg" element={<HrSoftwareJohannesburgPage />} />
      <Route path="/hr-software-cairo" element={<HrSoftwareCairoPage />} />
      <Route path="/hr-software-berlin" element={<HrSoftwareBerlinPage />} />
      <Route path="/hr-software-amsterdam" element={<HrSoftwareAmsterdamPage />} />
      <Route path="/hr-software-sao-paulo" element={<HrSoftwareSaoPauloPage />} />

      {/* Batch 5 — Countries */}
      <Route path="/hr-software-russia" element={<HrSoftwareRussiaPage />} />
      <Route path="/hr-software-peru-2" element={<HrSoftwarePeruPage2 />} />
      <Route path="/hr-software-bolivia" element={<HrSoftwareBoliviaPage />} />
      <Route path="/hr-software-paraguay" element={<HrSoftwareParaguayPage />} />
      <Route path="/hr-software-uruguay" element={<HrSoftwareUruguayPage />} />
      <Route path="/hr-software-mozambique" element={<HrSoftwareMozambiquePage />} />
      <Route path="/hr-software-cameroon" element={<HrSoftwareCameroonPage />} />
      <Route path="/hr-software-ivory-coast" element={<HrSoftwareIvoryCoastPage />} />
      <Route path="/hr-software-tunisia" element={<HrSoftwareTunisiaPage />} />
      <Route path="/hr-software-algeria" element={<HrSoftwareAlgeriaPage />} />
      <Route path="/hr-software-libya" element={<HrSoftwareLibyaPage />} />
      <Route path="/hr-software-lebanon" element={<HrSoftwareLebanonPage />} />
      <Route path="/hr-software-afghanistan" element={<HrSoftwareAfghanistanPage />} />
      <Route path="/hr-software-laos" element={<HrSoftwareLaosPage />} />
      <Route path="/hr-software-uzbekistan" element={<HrSoftwareUzbekistanPage />} />

      {/* Batch 5 — Cities */}
      <Route path="/hr-software-atlanta" element={<HrSoftwareAtlantaPage />} />
      <Route path="/hr-software-miami" element={<HrSoftwareMiamiPage />} />
      <Route path="/hr-software-seattle" element={<HrSoftwareSeattlePage />} />
      <Route path="/hr-software-boston" element={<HrSoftwareBostonPage />} />
      <Route path="/hr-software-phoenix" element={<HrSoftwarePhoenixPage />} />
      <Route path="/hr-software-dar-es-salaam" element={<HrSoftwareDarEsSalaamPage />} />
      <Route path="/hr-software-addis-ababa" element={<HrSoftwareAddisAbabaPage />} />
      <Route path="/hr-software-kampala" element={<HrSoftwareKampalaPage />} />
      <Route path="/hr-software-osaka" element={<HrSoftwareOsakaPage />} />
      <Route path="/hr-software-hanoi" element={<HrSoftwareHanoiPage />} />
      <Route path="/hr-software-islamabad" element={<HrSoftwareIslamabadPage />} />
      <Route path="/hr-software-lahore" element={<HrSoftwareLahorePage />} />
      <Route path="/hr-software-casablanca" element={<HrSoftwareCasablancaPage />} />
      <Route path="/hr-software-abuja" element={<HrSoftwareAbujaPage />} />
      <Route path="/hr-software-busan" element={<HrSoftwareBusanPage />} />
      <Route path="/hr-software-brisbane" element={<HrSoftwareBrisbanePage />} />
      <Route path="/hr-software-brisbane-qld" element={<HrSoftwareBrisbaneQldPage />} />
      <Route path="/hr-software-auckland" element={<HrSoftwareAucklandPage />} />
      <Route path="/hr-software-calgary" element={<HrSoftwareCalgaryPage />} />
      <Route path="/hr-software-mexico-city" element={<HrSoftwareMexicoCityPage />} />
      <Route path="/hr-software-milan" element={<HrSoftwareMilanPage />} />
      <Route path="/hr-software-vienna" element={<HrSoftwareViennaPage />} />
      <Route path="/hr-software-helsinki" element={<HrSoftwareHelsinkiPage />} />
      <Route path="/hr-software-athens" element={<HrSoftwareAthensPage />} />
      <Route path="/hr-software-kolkata" element={<HrSoftwareKolkataPage />} />
      <Route path="/hr-software-patna" element={<HrSoftwarePatnaPage />} />
      <Route path="/hr-software-bhubaneswar" element={<HrSoftwareBhubaneswarPage />} />
      <Route path="/hr-software-guwahati" element={<HrSoftwareGuwahatiPage />} />
      <Route path="/hr-software-jodhpur" element={<HrSoftwareJodhpurPage />} />
      <Route path="/hr-software-nashik" element={<HrSoftwareNashikPage />} />
      <Route path="/hr-software-madurai" element={<HrSoftwareMaduraiPage />} />
      <Route path="/hr-software-thiruvananthapuram" element={<HrSoftwareThiruvananthapuramPage />} />
      <Route path="/hr-software-raipur" element={<HrSoftwareRaipurPage />} />
      <Route path="/hr-software-dehradun" element={<HrSoftwareDehradunPage />} />
      <Route path="/hr-software-mysore" element={<HrSoftwareMysurePage />} />
      <Route path="/hr-software-mangalore" element={<HrSoftwareMangalorePage />} />
      <Route path="/hr-software-vijayawada" element={<HrSoftwareVijayawadaPage />} />
      <Route path="/hr-software-tiruchirappalli" element={<HrSoftwareTiruchirapalliPage />} />
      <Route path="/hr-software-salem" element={<HrSoftwareSalemPage />} />
      <Route path="/hr-software-rajkot" element={<HrSoftwareRajkotPage />} />
      <Route path="/hr-software-amritsar" element={<HrSoftwareAmritsarPage />} />
      <Route path="/hr-software-udaipur" element={<HrSoftwareUdaipurPage />} />
      <Route path="/hr-software-hubli" element={<HrSoftwareHubliPage />} />
      <Route path="/hr-software-aurangabad" element={<HrSoftwareAurangabadPage />} />
      <Route path="/hr-software-ranchi" element={<HrSoftwareRanchiPage />} />
      <Route path="/hr-software-navi-mumbai" element={<HrSoftwareNaviMumbaiPage />} />
      <Route path="/hr-software-thane" element={<HrSoftwareThanePage />} />
      <Route path="/hr-software-siliguri" element={<HrSoftwareSiliguriPage />} />
      <Route path="/hr-software-west-bengal" element={<HrSoftwareWestBengalPage />} />
      <Route path="/hr-software-bihar" element={<HrSoftwareBiharPage />} />
      <Route path="/hr-software-odisha" element={<HrSoftwareOdishaPage />} />
      <Route path="/hr-software-assam" element={<HrSoftwareAssamPage />} />
      <Route path="/hr-software-jharkhand" element={<HrSoftwareJharkhandPage />} />
      <Route path="/hr-software-chhattisgarh" element={<HrSoftwareChhattisgarhPage />} />
      <Route path="/hr-software-andhra-pradesh" element={<HrSoftwareAndhraPradeshPage />} />
      <Route path="/hr-software-madhya-pradesh" element={<HrSoftwareMadhyaPradeshPage />} />
      <Route path="/hr-software-goa" element={<HrSoftwareGoaPage />} />
      <Route path="/hr-software-himachal-pradesh" element={<HrSoftwareHimachalPradeshPage />} />
      <Route path="/hr-software-uttarakhand" element={<HrSoftwareUttarakhandPage />} />
      <Route path="/hr-software-tripura" element={<HrSoftwareTripuraPage />} />
      <Route path="/hr-software-gwalior" element={<HrSoftwareGwaliorPage />} />
      <Route path="/hr-software-jabalpur" element={<HrSoftwareJabalpurPage />} />
      <Route path="/hr-software-kolhapur" element={<HrSoftwareKolhapurPage />} />
      <Route path="/hr-software-pimpri-chinchwad" element={<HrSoftwarePimpriChinchwadPage />} />
      <Route path="/hr-software-shimla" element={<HrSoftwareShimlaPage />} />
      <Route path="/hr-software-jammu" element={<HrSoftwareJammuPage />} />
      <Route path="/hr-software-durgapur" element={<HrSoftwareDurgapurPage />} />
      <Route path="/hr-software-san-jose" element={<HrSoftwareSanJosePage />} />
      <Route path="/hr-software-austin" element={<HrSoftwareAustinPage />} />
      <Route path="/hr-software-denver" element={<HrSoftwareDenverPage />} />
      <Route path="/hr-software-portland" element={<HrSoftwarePortlandPage />} />
      <Route path="/hr-software-nashville" element={<HrSoftwareNashvillePage />} />
      <Route path="/hr-software-charlotte" element={<HrSoftwareCharlottePage />} />
      <Route path="/hr-software-raleigh" element={<HrSoftwareRaleighPage />} />
      <Route path="/hr-software-minneapolis" element={<HrSoftwareMinneapolisPage />} />
      <Route path="/hr-software-new-jersey" element={<HrSoftwareNewJerseyPage />} />
      <Route path="/hr-software-washington-dc" element={<HrSoftwareWashingtonDcPage />} />
      <Route path="/hr-software-tampa" element={<HrSoftwareTampaPage />} />
      <Route path="/hr-software-san-diego" element={<HrSoftwareSanDiegoPage />} />
      <Route path="/hr-software-las-vegas" element={<HrSoftwareLasVegasPage />} />
      <Route path="/hr-software-sacramento" element={<HrSoftwareSacramentoPage />} />
      <Route path="/hr-software-pittsburgh" element={<HrSoftwarePittsburghPage />} />
      <Route path="/hr-software-columbus" element={<HrSoftwareColumbusPage />} />
      <Route path="/hr-software-detroit" element={<HrSoftwareDetroitPage />} />
      <Route path="/hr-software-kansas-city" element={<HrSoftwareKansasCityPage />} />
      <Route path="/hr-software-indianapolis" element={<HrSoftwareIndianapolisPage />} />
      <Route path="/hr-software-orlando" element={<HrSoftwareOrlandoPage />} />

      {/* Blog admin — protected */}
      <Route path="/hr/blog" element={<ProtectedRoute roles={['hr','admin']}><MainLayout><BlogManage /></MainLayout></ProtectedRoute>} />
      <Route path="/hr/blog/new" element={<ProtectedRoute roles={['hr','admin']}><MainLayout><BlogEditor /></MainLayout></ProtectedRoute>} />
      <Route path="/hr/blog/edit/:slug" element={<ProtectedRoute roles={['hr','admin']}><MainLayout><BlogEditor /></MainLayout></ProtectedRoute>} />
      <Route path="/hr/blog/access" element={<ProtectedRoute roles={['admin']}><MainLayout><BlogAccess /></MainLayout></ProtectedRoute>} />

      {/* Blog — employee writers (access controlled by canBlog on the backend) */}
      <Route path="/employee/blog" element={<ProtectedRoute roles={['employee']}><MainLayout><BlogManage /></MainLayout></ProtectedRoute>} />
      <Route path="/employee/blog/new" element={<ProtectedRoute roles={['employee']}><MainLayout><BlogEditor /></MainLayout></ProtectedRoute>} />
      <Route path="/employee/blog/edit/:slug" element={<ProtectedRoute roles={['employee']}><MainLayout><BlogEditor /></MainLayout></ProtectedRoute>} />

      {/* Reviews — public page + admin moderation */}
      <Route path="/reviews" element={<ReviewsPage />} />
      <Route path="/hr/reviews" element={<ProtectedRoute roles={['admin']}><MainLayout><ReviewsManage /></MainLayout></ProtectedRoute>} />

      {/* Website traffic analytics — admin only */}
      <Route path="/hr/site-analytics" element={<ProtectedRoute roles={['admin']}><MainLayout><SiteAnalytics /></MainLayout></ProtectedRoute>} />
      <Route path="/hr/calculator-report" element={<ProtectedRoute roles={['admin', 'hr']}><MainLayout><CalculatorReport /></MainLayout></ProtectedRoute>} />
      <Route path="/hr/documents" element={<ProtectedRoute roles={['admin', 'hr']}><MainLayout><HrDocuments /></MainLayout></ProtectedRoute>} />
      <Route path="/hr/attempts" element={<ProtectedRoute roles={['admin']}><MainLayout><LoginAttempts /></MainLayout></ProtectedRoute>} />
      <Route path="/hr/ai-providers" element={<ProtectedRoute roles={['admin','hr']}><MainLayout><AiProvidersPage /></MainLayout></ProtectedRoute>} />
      <Route path="/admin/pdf-revenue" element={<ProtectedRoute roles={['admin']}><MainLayout><PremiumPdfRevenuePage /></MainLayout></ProtectedRoute>} />
      <Route path="/hr/pdf-revenue" element={<ProtectedRoute roles={['admin']}><MainLayout><PremiumPdfRevenuePage /></MainLayout></ProtectedRoute>} />

      {/* Blog */}
      <Route path="/blog" element={<BlogIndex />} />
      <Route path="/blog/:slug" element={<BlogPost />} />

      <Route
        path="/hr/tasks"
        element={
          <ProtectedRoute roles={['hr', 'admin']}>
            <MainLayout>
              <TaskManagementPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/hr/task-performance"
        element={
          <ProtectedRoute roles={['hr', 'admin']}>
            <MainLayout>
              <TaskPerformancePage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Employee Protected Routes */}
      <Route
        path="/employee/dashboard"
        element={<ProtectedRoute roles={['employee']}><MainLayout><EmployeeDashboard /></MainLayout></ProtectedRoute>}
      />
      <Route
        path="/employee/profile"
        element={<ProtectedRoute roles={['employee']}><MainLayout><EmployeeProfile /></MainLayout></ProtectedRoute>}
      />
      <Route
        path="/employee/announcements"
        element={<ProtectedRoute roles={['employee']}><MainLayout><EmployeeAnnouncements /></MainLayout></ProtectedRoute>}
      />
      <Route
        path="/employee/apply-leave"
        element={<ProtectedRoute roles={['employee']}><MainLayout><ApplyForLeavePage /></MainLayout></ProtectedRoute>}
      />
      <Route
        path="/employee/rankings"
        element={<ProtectedRoute roles={['employee']}><MainLayout><RankingsPage /></MainLayout></ProtectedRoute>}
      />
      <Route
        path="/employee/salary-slips"
        element={<ProtectedRoute roles={['employee']}><MainLayout><EmployeeSalarySlips /></MainLayout></ProtectedRoute>}
      />

      {/* HR Protected Routes */}


      <Route
        path="/hr/dashboard"
        element={<ProtectedRoute roles={['hr', 'admin']}><MainLayout><HRDashboard /></MainLayout></ProtectedRoute>}
      />
      <Route
        path="/hr/employee/:id"
        element={<ProtectedRoute roles={['hr', 'admin']}><HREmployeeView /></ProtectedRoute>}
      />
      <Route
        path="/hr/eod-reports"
        element={<ProtectedRoute roles={['hr', 'admin']}><EODReportsPage /></ProtectedRoute>}
      />
      <Route
        path="/hr/manage-employees"
        element={<ProtectedRoute roles={['hr', 'admin']}><ManageEmployeesPage /></ProtectedRoute>}
      />
      <Route
        path="/hr/salary-calculator"
        element={<ProtectedRoute roles={['hr', 'admin']}><SalaryCalculatorPage /></ProtectedRoute>}
      />
      <Route
        path="/hr/announcements"
        element={<ProtectedRoute roles={['hr', 'admin']}><AnnouncementsPage /></ProtectedRoute>}
      />
      <Route
        path="/hr/analytics"
        element={<ProtectedRoute roles={['hr', 'admin']}><AnalyticsDashboard /></ProtectedRoute>}
      />
      <Route
        path="/hr/partners"
        element={<ProtectedRoute roles={['hr', 'admin']}><MainLayout><PartnerManagementPage /></MainLayout></ProtectedRoute>}
      />
      {/* --- NEW HR RANKINGS ROUTE --- */}
      <Route
        path="/hr/rankings"
        element={<ProtectedRoute roles={['hr', 'admin']}><MainLayout><RankingsPage /></MainLayout></ProtectedRoute>}
      />
      <Route
        path="/hr/getdata"
        element={<ProtectedRoute roles={['hr', 'admin']}><GetDataPage /></ProtectedRoute>}
      />
      <Route
        path="/employee/tasks"
        element={
          <ProtectedRoute roles={['employee']}>
            <MainLayout>
              <TaskManagementPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Leads — admin only */}
      <Route
        path="/hr/leads"
        element={
          <ProtectedRoute roles={['admin']}>
            <MainLayout>
              <LeadsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/employee/leads"
        element={
          <ProtectedRoute roles={['employee']}>
            <MainLayout>
              <LeadsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Meetings — shared page, role-aware on the inside */}
      <Route
        path="/hr/meetings"
        element={
          <ProtectedRoute roles={['hr', 'admin']}>
            <MainLayout>
              <MeetingsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/employee/meetings"
        element={
          <ProtectedRoute roles={['employee']}>
            <MainLayout>
              <MeetingsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Expenses — shared page, role-aware on the inside */}
      <Route
        path="/hr/expenses"
        element={
          <ProtectedRoute roles={['hr', 'admin']}>
            <MainLayout>
              <ExpensesPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/employee/expenses"
        element={
          <ProtectedRoute roles={['employee']}>
            <MainLayout>
              <ExpensesPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Projects — shared page, role-aware on the inside */}
      <Route
        path="/hr/projects"
        element={
          <ProtectedRoute roles={['hr', 'admin']}>
            <MainLayout>
              <ProjectsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/employee/projects"
        element={
          <ProtectedRoute roles={['employee']}>
            <MainLayout>
              <ProjectsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Assets — shared page, role-aware on the inside */}
      <Route
        path="/hr/assets"
        element={
          <ProtectedRoute roles={['hr', 'admin']}>
            <MainLayout>
              <AssetsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/employee/assets"
        element={
          <ProtectedRoute roles={['employee']}>
            <MainLayout>
              <AssetsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Invoices — admin/HR/managers only */}
      <Route
        path="/hr/invoices"
        element={
          <ProtectedRoute roles={['hr', 'admin']}>
            <MainLayout>
              <InvoicesPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/employee/invoices"
        element={
          <ProtectedRoute roles={['employee']}>
            <MainLayout>
              <InvoicesPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* KPIs — visible to admin / HR / managers (manager scope enforced server-side) */}
      <Route
        path="/hr/kpis"
        element={
          <ProtectedRoute roles={['hr', 'admin']}>
            <MainLayout>
              <KPIsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/employee/kpis"
        element={
          <ProtectedRoute roles={['employee']}>
            <MainLayout>
              <KPIsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Finance Tracker — admin only (page also self-gates as belt-and-braces) */}
      <Route
        path="/hr/finance"
        element={
          <ProtectedRoute roles={['admin']}>
            <MainLayout>
              <FinancePage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      {/* Finance Tracker — employees granted access by admin */}
      <Route
        path="/employee/finance"
        element={
          <ProtectedRoute roles={['employee']}>
            <MainLayout>
              <FinancePage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Calls Diary — personal call notebook, private per user. */}
      <Route
        path="/admin/calls"
        element={
          <ProtectedRoute roles={['admin', 'hr', 'employee']}>
            <MainLayout>
              <CallsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/employee/calls"
        element={
          <ProtectedRoute roles={['employee']}>
            <MainLayout>
              <CallsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Sales Targets — admin/HR see all, managers see their team, employees see their own */}
      <Route
        path="/hr/targets"
        element={
          <ProtectedRoute roles={['hr', 'admin']}>
            <MainLayout>
              <TargetsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/employee/targets"
        element={
          <ProtectedRoute roles={['employee']}>
            <MainLayout>
              <TargetsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Portal Requests — admin / HR / managers triage incoming homepage requests */}
      <Route
        path="/hr/portal-requests"
        element={
          <ProtectedRoute roles={['hr', 'admin']}>
            <MainLayout>
              <PortalRequestsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/employee/portal-requests"
        element={
          <ProtectedRoute roles={['employee']}>
            <MainLayout>
              <PortalRequestsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/approve-leave"
        element={<ProtectedRoute roles={['hr', 'admin']}><ApproveLeavePage /></ProtectedRoute>}
      />
      <Route
        path="/hr/penalties"
        element={
          <ProtectedRoute roles={['hr', 'admin']}>
            <MainLayout>
              <PenaltiesPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/salary-slips"
        element={
          <ProtectedRoute roles={['hr', 'admin']}>
            <MainLayout>
              <SalarySlipManagement />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/register-employee"
        element={
          <ProtectedRoute roles={['hr', 'admin']}>
            <MainLayout>
              <RegisterEmployeeHRPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/managers"
        element={
          <ProtectedRoute roles={['hr', 'admin']}>
            <MainLayout>
              <ManageManagersPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      {/* Partner Routes */}
      <Route
        path="/partner/dashboard"
        element={
          <ProtectedRoute roles={['partner']}>
            <MainLayout>
              <PartnerDashboard />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/partner-commissions"
        element={
          <ProtectedRoute roles={['hr', 'admin']}>
            <MainLayout>
              <PartnerCommissionsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Email CRM — admin/hr and employees with leads access */}
      <Route
        path="/hr/email"
        element={
          <ProtectedRoute roles={['hr', 'admin']}>
            <MainLayout><EmailPage /></MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/employee/email"
        element={
          <ProtectedRoute roles={['employee']}>
            <MainLayout><EmailPage /></MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Holiday Calendar — HR/admin manage, employees view read-only */}
      <Route path="/hr/holiday-calendar" element={
        <ProtectedRoute roles={['hr', 'admin']}>
          <MainLayout><HolidayCalendarPage /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/employee/holiday-calendar" element={
        <ProtectedRoute roles={['employee']}>
          <MainLayout><HolidayCalendarPage /></MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/hr/whatsapp" element={
        <ProtectedRoute roles={['hr', 'admin']}>
          <MainLayout><WhatsAppManagePage /></MainLayout>
        </ProtectedRoute>
      } />

      {/* Catch-all Not Found Route */}
      <Route path="/ai-chatbot-for-hr" element={<AiChatbotForHrPage />} />
      <Route path="/conversational-hr-software" element={<ConversationalHrSoftwarePage />} />
      <Route path="/ai-hr-chatbot" element={<AiHrChatbotPage />} />
      <Route path="/natural-language-hr-query" element={<NaturalLanguageHrQueryPage />} />
      <Route path="/ai-employee-self-service" element={<AiEmployeeSelfServicePage />} />
      <Route path="/ai-payroll-software" element={<AiPayrollSoftwarePage />} />
      <Route path="/ai-attendance-system" element={<AiAttendanceSystemPage />} />
      <Route path="/ai-leave-management" element={<AiLeaveManagementPage />} />
      <Route path="/ai-hr-automation" element={<AiHrAutomationPage />} />
      <Route path="/ai-payroll-india" element={<AiPayrollIndiaPage />} />
      <Route path="/ai-crm-software" element={<AiCrmSoftwarePage />} />
      <Route path="/ai-sales-assistant" element={<AiSalesAssistantPage />} />
      <Route path="/ai-lead-management" element={<AiLeadManagementPage />} />
      <Route path="/ai-meeting-assistant" element={<AiMeetingAssistantPage />} />
      <Route path="/ai-sales-crm-india" element={<AiSalesCrmIndiaPage />} />
      <Route path="/ai-task-management" element={<AiTaskManagementPage />} />
      <Route path="/ai-project-management" element={<AiProjectManagementPage />} />
      <Route path="/ai-business-management-software" element={<AiBusinessManagementSoftwarePage />} />
      <Route path="/ai-erp-software" element={<AiErpSoftwarePage />} />
      <Route path="/ai-okr-software" element={<AiOkrSoftwarePage />} />
      <Route path="/ai-hr-analytics" element={<AiHrAnalyticsPage />} />
      <Route path="/ai-hr-reporting" element={<AiHrReportingPage />} />
      <Route path="/ai-performance-management" element={<AiPerformanceManagementPage />} />
      <Route path="/ai-workforce-analytics" element={<AiWorkforceAnalyticsPage />} />
      <Route path="/ai-report-generator" element={<AiReportGeneratorPage />} />
      <Route path="/ai-hr-software-for-small-business" element={<AiHrForSmallBusinessPage />} />
      <Route path="/ai-hr-software-for-startups" element={<AiHrForStartupsPage />} />
      <Route path="/ai-hr-software-for-enterprises" element={<AiHrForEnterprisesPage />} />
      <Route path="/ai-hr-software-for-agencies" element={<AiHrForAgenciesPage />} />
      <Route path="/generative-ai-for-hr" element={<GenerativeAiForHrPage />} />
      <Route path="/ai-hr-software-dubai" element={<AiHrSoftwareDubaiPage />} />
      <Route path="/ai-hr-software-usa" element={<AiHrSoftwareUsaPage />} />
      <Route path="/ai-hr-software-uk" element={<AiHrSoftwareUkPage />} />
      <Route path="/ai-copilot-for-business" element={<AiCopilotForBusinessPage />} />
      <Route path="/ai-hr-helpdesk" element={<AiHrHelpdeskPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </Suspense>
  );
};

export default AppRouter;
